import { AbstractControl, ValidatorFn } from '@angular/forms';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import patterns from 'utils/patterns';
import {
  countryLookup,
  marketingCodeLookup,
  marketingTypeLookup,
  yesNoLookup,
} from 'utils/lookups';
import {
  lifeCycleStatusLookup,
  nationalPopularityCodeLookup,
  whmisCodeLookup,
} from 'shared/product/product-lookups';
import { digitalAssetsSegmentData } from 'shared/digital-assets/digital-assets-segment-data';
import { isLookupOption } from 'shared/validation/select-validation';
import { descriptionSegmentData } from '../product-details-v2/segment-data/description-segment-data';

const weightVarianceValidationText = 'Weight variance (%) must be greater than 0 and less than 8 digits including 4 decimal places.';
const dimensionalWeightValidationText = 'Dimensional weight must be greater than 0 and less than 9 characters including 4 decimal places.';

/* eslint-disable */
export const segmentsData: any = {
  data: null,
  'items_details' : [
    {
      'field_map': 'partNumber',
      'item_name': 'partNumber',
      'title': 'Part no.',
      'hint_detail': 'Manufacturers determined part number',
      'hint_panel' : 'Manufacturers determined part number',
      'example': 'HOS101',
      'sequence': 1,
      'show_more': true,
      'validation': {
        'PIES_6_5' : {
          required: true,
          minLength: 1,
          maxLength: 48,
          message: 'Part number is required and must be no more than 48 characters'
        }
      },
      'lookup': null,
      'not_bulk_editable': true,
    },
    {
      'field_map': 'gtin',
      'item_name': 'details.gtin',
      'title': 'GTIN',
      'hint_detail': 'Global Trade Item Number (GTIN) can be used by a company to uniquely identify all of its trade items. The GTIN number can be based on Universal Product Code (UPC) or International Article Number (EAN).',
      'hint_panel' : 'Global Trade Item Number (GTIN) can be used by a company to uniquely identify all of its trade items. The GTIN number can be based on Universal Product Code (UPC) or International Article Number (EAN).',
      'example': '00024844170316',
      'sequence': 2,
      'show_more': true,
      'lookup': null,
      'not_bulk_editable': true,
      'numerical': true,
      'validation' : {
        'PIES_6_5': {
          'length': 14,
          'message': 'GTIN must be exactly 14 characters.'
        }
      },
    },
    {
      'field_map': 'gtinQualifier',
      'item_name': 'details.gtinQualifier',
      'title': 'UPC/EAN code',
      'hint_detail': 'UPC/EAN : A 12 digit number identifying the manufacturers shipping unit. The code division is 2-5-5. Its qualifier is UA The code is formatted as: 2 digit number system character(U.S. Grocery and General Merchandise=00,06 or 07; US Drug=03)(EAN=2 digit country flag); 5 digit manufacturing identification number; 5 digit item number',
      'hint_panel' : 'UPC/EAN : A 12 digit number identifying the manufacturers shipping unit. The code division is 2-5-5. Its qualifier is UA The code is formatted as: 2 digit number system character(U.S. Grocery and General Merchandise=00,06 or 07; US Drug=03)(EAN=2 digit country flag); 5 digit manufacturing identification number; 5 digit item number',
      'example': 'UPC',
      'sequence': 3,
      'show_more': true,
      'validation': {
        'PIES_6_5': {
          'custom': function (): ValidatorFn {
            return (control: AbstractControl): {[key: string]: any} => {
              if (this.detailFormGroup && this.detailFormGroup.controls['gtin']){
                  if (
                    this.detailFormGroup.controls['gtin'].value.length &&
                    !control.value.length
                  ) {
                    return {'singGTIN': 'GTIN REQUIRED'};
                  }
              }
              return null;
            };
          },
          'required': true,
          'message': 'UPC/EAN code is required when GTIN is populated.'
        }
      },
      'lookup': [
        {
          'option': '',
          'title': '-'
        },
        {
          'option': 'UP',
          'title': 'UPC'
        },
        {
          'option': 'EN',
          'title': 'EAN'
        }
      ],
      'not_bulk_editable': true,
    },
    {
      'field_map': 'brandLabel',
      'item_name': 'details.brandLabel',
      'title': 'Brand',
      'hint_detail': 'Brand name corresponding with Brand ID found in the Auto Care maintained Parent/Supplier/Brand Code Registry',
      'hint_panel' : 'Brand name corresponding with Brand ID found in the Auto Care maintained Parent/Supplier/Brand Code Registry',
      'example': 'Wonderhose',
      'sequence': 4,
      'show_more': true,
      'validation' : {
        'PIES_6_5': {
          'required': true,
          'minLength': 1,
          'maxLength': 60,
          'message': 'Brand label is required and must be no more than 60 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'hasFitments',
      'item_name': 'details.hasFitments',
      'title': 'Vehicle fitments',
      'hint_detail': 'YES if this item has vehicle fitments (ACES applications) or NO if this item is universal',
      'hint_panel' : 'Select YES if this item has vehicle fitments (ACES applications) or NO if this item is universal',
      'example': 'Yes',
      'sequence': 5,
      'show_more': true,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 1,
        }
      },
      'lookup': [
        {
          'option': '',
          'title': '-'
        },
        {
          'option': 'Y',
          'title': 'Yes'
        },
        {
          'option': 'N',
          'title': 'No'
        }
      ]
    },
    {
      'field_map': 'partType',
      'item_name': 'partTerminology.id',
      'title': 'Part ID',
      'hint_detail': 'Part ID, maintained by the Auto Care Association within the Part Classification database (PCdb).',
      'hint_panel' : 'Select the appropriate part ID for this item. Part IDs are maintained by the Auto Care Association within the Part Classification database (PCdb).',
      'example': '12345',
      'sequence': 6,
      'show_more': true,
      'lookup': null,
      'api_lookup': {
          'type': 'typeahead',
          'field_map': 'partType',
          'item_name': 'partTerminology.id',
          'lookup': 'part-id',
      }
    },
    {
      'field_map': 'partTypeName',
      'item_name': 'partTerminology.name',
      'title': 'Part type',
      'hint_detail': 'Part type, also known as part terminology, maintained by the Auto Care Association within the Part Classification database (PCdb).',
      'hint_panel' : 'Select the appropriate part type (aka, part terminology) for this item. Part types are maintained by the Auto Care Association within the Part Classification database (PCdb).',
      'example': 'Air Filter',
      'sequence': 6,
      'show_more': true,
      'lookup': null,
      'api_lookup': {
          'type': 'typeahead',
          'field_map': 'partTypeName',
          'item_name': 'partTerminology.name',
          'lookup': 'part-type',
      }
    },
    {
      'field_map': 'partCategory',
      'item_name': 'partTerminology.category.name',
      'title': 'Category',
      'hint_detail': 'Category, maintained by the Auto Care Association within the Part Classification database (PCdb).',
      'hint_panel' : 'Select the appropriate category for this item. Categories are maintained by the Auto Care Association within the Part Classification database (PCdb).',
      'example': '',
      'sequence': 6,
      'show_more': true,
      'lookup': null,
      'api_lookup': {
          'type': 'typeahead',
          'field_map': 'partCategory',
          'item_name': 'partTerminology.category.name',
          'lookup': 'part-category',
      }
    },
    {
      'field_map': 'partSubcategory',
      'item_name': 'partTerminology.subcategory.name',
      'title': 'Sub category',
      'hint_detail': 'Sub category, maintained by the Auto Care Association within the Part Classification database (PCdb).',
      'hint_panel' : 'Select the appropriate sub category for this item. Sub categories are maintained by the Auto Care Association within the Part Classification database (PCdb).',
      'example': '',
      'sequence': 6,
      'show_more': true,
      'lookup': null,
      'api_lookup': {
        'type': 'typeahead',
        'field_map': 'partSubcategory',
        'item_name': 'partTerminology.subcategory.name',
        'lookup': 'part-subcategory',
      }
    },
    {
      'field_map': 'nationalPopularityCode',
      'item_name': 'details.nationalPopularityCode',
      'group': 'nat_pop_code',
      'title': 'National popularity code',
      'hint_detail': 'Indicates the product velocity (i.e., days or weeks a product stays in warehouse inventory before being sold).',
      'hint_panel' : 'Select the appropriate velocity level for the part number.',
      'example': '(B) Next 20% of Product group sales value',
      'sequence': 7,
      'show_more': true,
      'lookup': nationalPopularityCodeLookup,
    },
    {
      'field_map': 'productGroupId',
      'item_name': 'details.group.id',
      'title': 'Product group',
      'hint_detail': 'Indicates which Product Group this item belongs in. The Product Group is maintained at the Global account level.',
      'hint_panel' : 'Select the Product Group or series this item belongs in. The Product Group is defined and maintained at the Global account level.',
      'example': 'HS100',
      'sequence': 8,
      'show_more': true,
      'lookup': null
    },
    {
      'field_map': 'productSubGroupId',
      'item_name': 'details.group.subGroup.id',
      'title': 'Product sub group',
      'hint_detail': 'Indicates which Product SubGroup this item belongs in. The Product SubGroup is maintained at the Global account level.',
      'hint_panel' : 'Select the Product SubGroup or sub-series this item belongs in. The Product SubGroup is defined and maintained at the Global account level.',
      'example': 'HS110',
      'sequence': 8,
      'show_more': true,
      'lookup': null
    },
    {
      'field_map': 'lifeCycleStatus',
      'item_name': 'details.lifeCycleStatus',
      'title': 'Life cycle status',
      'hint_detail': 'Indicates this parts current stage in the release cycle.',
      'hint_panel' : 'Select the current stage the part is within.',
      'sequence': 8,
      'show_more': true,
      'lookup': lifeCycleStatusLookup,
    },
    {
      'field_map': 'minimumOrderQuantity',
      'item_name': 'details.minimumOrderQuantity',
      'title': 'Minimum order quantity',
      'hint_detail': 'Minimum (incremental) shipment quantity of this item.',
      'hint_panel' : 'Minimum (incremental) shipment quantity of this item.',
      'example': '10',
      'sequence': 9,
      'show_more': true,
      'lookup': null,
      'numerical': true,
      'validation': {
        'PIES_6_5' : {
          max: 99999999,
          message: 'Minimum order quantity must be less then 99999999.'
        }
      },
    },
    {
      'field_map': 'minimumOrderQuantityUom',
      'item_name': 'details.minimumOrderQuantityUom',
      'title': 'Units of measure',
      'hint_detail': 'Unit of measure for the minimum shipment quantity.',
      'hint_panel' : 'Indicate the unit of measure for the minimum shipment quantity. "Each" is the most commonly used value.',
      'example': 'Each',
      'sequence': 10,
      'show_more': true,
      'lookup': [
        { 'option': '', 'title': '-' },
        { 'option': 'OZ', 'title': 'Ounces' },
        { 'option': 'AS', 'title': 'Assortment' },
        { 'option': 'BG', 'title': 'Bag' },
        { 'option': 'BK', 'title': 'Book' },
        { 'option': 'BO', 'title': 'Bottle' },
        { 'option': 'BX', 'title': 'Box' },
        { 'option': 'BC', 'title': 'Bucket' },
        { 'option': 'BD', 'title': 'Bundle' },
        { 'option': 'CN', 'title': 'Can' },
        { 'option': 'CG', 'title': 'Card' },
        { 'option': 'CT', 'title': 'Carton' },
        { 'option': 'CQ', 'title': 'Cartridge' },
        { 'option': 'CA', 'title': 'Case' },
        { 'option': 'CX', 'title': 'Coil' },
        { 'option': 'CH', 'title': 'Container' },
        { 'option': 'FN', 'title': 'Clamshell' },
        { 'option': 'CP', 'title': 'Crate' },
        { 'option': 'DZ', 'title': 'Dozen' },
        { 'option': 'DR', 'title': 'Drum' },
        { 'option': 'EA', 'title': 'Each' },
        { 'option': 'EV', 'title': 'Envelope' },
        { 'option': 'FT', 'title': 'Foot' },
        { 'option': 'GA', 'title': 'Gallon' },
        { 'option': 'HU', 'title': 'Hundred' },
        { 'option': 'HC', 'title': 'Hundred count' },
        { 'option': 'JR', 'title': 'Jar' },
        { 'option': 'KE', 'title': 'Keg' },
        { 'option': 'KG', 'title': 'Kilogram' },
        { 'option': 'KT', 'title': 'Kit' },
        { 'option': 'LT', 'title': 'Liter' },
        { 'option': 'MR', 'title': 'Meter' },
        { 'option': 'PH', 'title': 'Pack (PAK)' },
        { 'option': 'PK', 'title': 'Package' },
        { 'option': 'PA', 'title': 'Pail' },
        { 'option': 'PR', 'title': 'Pair' },
        { 'option': 'PF', 'title': 'Pallet' },
        { 'option': 'PL', 'title': 'Pallet unit load' },
        { 'option': 'PC', 'title': 'Piece' },
        { 'option': 'PT', 'title': 'Pint' },
        { 'option': 'LB', 'title': 'Pound' },
        { 'option': 'QT', 'title': 'Quart' },
        { 'option': 'RE', 'title': 'Reel' },
        { 'option': 'RD', 'title': 'Rod' },
        { 'option': 'RL', 'title': 'Roll' },
        { 'option': 'ST', 'title': 'Set' },
        { 'option': 'SH', 'title': 'Sheet' },
        { 'option': 'SL', 'title': 'Sleeve' },
        { 'option': 'SO', 'title': 'Spool' },
        { 'option': '15', 'title': 'Stick' },
        { 'option': 'TH', 'title': 'Thousand' },
        { 'option': 'TY', 'title': 'Tray' },
        { 'option': 'TB', 'title': 'Tube' },
        { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
      'field_map': 'containerType',
      'item_name': 'details.containerType',
      'title': 'Container type',
      'hint_detail': 'Describes the packaging for this item.',
      'hint_panel' : 'Choose the container packaging which describes this item.',
      'example': 'Box',
      'sequence': 10,
      'show_more': true,
      'lookup': [
        { 'option': '', 'title': '-' },
        { 'option': 'OZ', 'title': 'Ounces' },
        { 'option': 'AS', 'title': 'Assortment' },
        { 'option': 'BG', 'title': 'Bag' },
        { 'option': 'BK', 'title': 'Book' },
        { 'option': 'BO', 'title': 'Bottle' },
        { 'option': 'BX', 'title': 'Box' },
        { 'option': 'BC', 'title': 'Bucket' },
        { 'option': 'BD', 'title': 'Bundle' },
        { 'option': 'CN', 'title': 'Can' },
        { 'option': 'CG', 'title': 'Card' },
        { 'option': 'CT', 'title': 'Carton' },
        { 'option': 'CQ', 'title': 'Cartridge' },
        { 'option': 'CA', 'title': 'Case' },
        { 'option': 'CX', 'title': 'Coil' },
        { 'option': 'CH', 'title': 'Container' },
        { 'option': 'FN', 'title': 'Clamshell' },
        { 'option': 'CP', 'title': 'Crate' },
        { 'option': 'DZ', 'title': 'Dozen' },
        { 'option': 'DR', 'title': 'Drum' },
        { 'option': 'EA', 'title': 'Each' },
        { 'option': 'EV', 'title': 'Envelope' },
        { 'option': 'FT', 'title': 'Foot' },
        { 'option': 'GA', 'title': 'Gallon' },
        { 'option': 'HU', 'title': 'Hundred' },
        { 'option': 'HC', 'title': 'Hundred count' },
        { 'option': 'JR', 'title': 'Jar' },
        { 'option': 'KE', 'title': 'Keg' },
        { 'option': 'KG', 'title': 'Kilogram' },
        { 'option': 'KT', 'title': 'Kit' },
        { 'option': 'LT', 'title': 'Liter' },
        { 'option': 'MR', 'title': 'Meter' },
        { 'option': 'PH', 'title': 'Pack (PAK)' },
        { 'option': 'PK', 'title': 'Package' },
        { 'option': 'PA', 'title': 'Pail' },
        { 'option': 'PR', 'title': 'Pair' },
        { 'option': 'PF', 'title': 'Pallet' },
        { 'option': 'PL', 'title': 'Pallet unit load' },
        { 'option': 'PC', 'title': 'Piece' },
        { 'option': 'PT', 'title': 'Pint' },
        { 'option': 'LB', 'title': 'Pound' },
        { 'option': 'QT', 'title': 'Quart' },
        { 'option': 'RE', 'title': 'Reel' },
        { 'option': 'RD', 'title': 'Rod' },
        { 'option': 'RL', 'title': 'Roll' },
        { 'option': 'ST', 'title': 'Set' },
        { 'option': 'SH', 'title': 'Sheet' },
        { 'option': 'SL', 'title': 'Sleeve' },
        { 'option': 'SO', 'title': 'Spool' },
        { 'option': '15', 'title': 'Stick' },
        { 'option': 'TH', 'title': 'Thousand' },
        { 'option': 'TY', 'title': 'Tray' },
        { 'option': 'TB', 'title': 'Tube' },
        { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
      'field_map': 'itemQuantity',
      'item_name': 'details.itemQuantity',
      'title': 'Item quantity',
      'hint_detail': 'Clarifies the sizing level for the item.',
      'hint_panel' : 'Enter the sizing level for this item.',
      'example': 'If the item is a roll of hose, this could read as 3, and Units of measure could read as Foot, indicating that this item is sold as a 3 foot length roll of hose.',
      'sequence': 11,
      'show_more': true,
      'validation': {
        'PIES_6_5' : {
          min: 1,
          number: true,
          'custom': function (): ValidatorFn {
            return (control: AbstractControl): {[key: string]: any} => {
              let itemQuantity:string = control.value.replace('.','');
                if ( itemQuantity.length > 8 ) {
                  return {'itemQuantity': {valid: false}};
                }
              return null;
            };
          },
          message: 'Item quantity must be less then 99999999.'
        }
      },
      'lookup': null,
      'numerical': true,
      'allow_decimal': true,
    },
    {
      'field_map': 'itemQuantityUom',
      'item_name': 'details.itemQuantityUom',
      'title': 'Units of measure',
      'hint_detail': 'Indicates the unit of measure for the sizing level of the item.',
      'hint_panel' : 'Enter the unit of measure for the sizing level of the item.',
      'example': 'If the item is a roll of hose and the Item quantity was 3, this could read as Foot, indicating that this item is sold as a 3 foot length roll of hose.',
      'sequence': 11,
      'show_more': true,
      'lookup': [
        { 'option': '', 'title': '-' },
        { 'option': 'OZ', 'title': 'Ounces' },
        { 'option': 'AS', 'title': 'Assortment' },
        { 'option': 'BG', 'title': 'Bag' },
        { 'option': 'BK', 'title': 'Book' },
        { 'option': 'BO', 'title': 'Bottle' },
        { 'option': 'BX', 'title': 'Box' },
        { 'option': 'BC', 'title': 'Bucket' },
        { 'option': 'BD', 'title': 'Bundle' },
        { 'option': 'CN', 'title': 'Can' },
        { 'option': 'CG', 'title': 'Card' },
        { 'option': 'CT', 'title': 'Carton' },
        { 'option': 'CQ', 'title': 'Cartridge' },
        { 'option': 'CA', 'title': 'Case' },
        { 'option': 'CX', 'title': 'Coil' },
        { 'option': 'CH', 'title': 'Container' },
        { 'option': 'FN', 'title': 'Clamshell' },
        { 'option': 'CP', 'title': 'Crate' },
        { 'option': 'DZ', 'title': 'Dozen' },
        { 'option': 'DR', 'title': 'Drum' },
        { 'option': 'EA', 'title': 'Each' },
        { 'option': 'EV', 'title': 'Envelope' },
        { 'option': 'FT', 'title': 'Foot' },
        { 'option': 'GA', 'title': 'Gallon' },
        { 'option': 'HU', 'title': 'Hundred' },
        { 'option': 'HC', 'title': 'Hundred count' },
        { 'option': 'JR', 'title': 'Jar' },
        { 'option': 'KE', 'title': 'Keg' },
        { 'option': 'KG', 'title': 'Kilogram' },
        { 'option': 'KT', 'title': 'Kit' },
        { 'option': 'LT', 'title': 'Liter' },
        { 'option': 'MR', 'title': 'Meter' },
        { 'option': 'PH', 'title': 'Pack (PAK)' },
        { 'option': 'PK', 'title': 'Package' },
        { 'option': 'PA', 'title': 'Pail' },
        { 'option': 'PR', 'title': 'Pair' },
        { 'option': 'PF', 'title': 'Pallet' },
        { 'option': 'PL', 'title': 'Pallet unit load' },
        { 'option': 'PC', 'title': 'Piece' },
        { 'option': 'PT', 'title': 'Pint' },
        { 'option': 'LB', 'title': 'Pound' },
        { 'option': 'QT', 'title': 'Quart' },
        { 'option': 'RE', 'title': 'Reel' },
        { 'option': 'RD', 'title': 'Rod' },
        { 'option': 'RL', 'title': 'Roll' },
        { 'option': 'ST', 'title': 'Set' },
        { 'option': 'SH', 'title': 'Sheet' },
        { 'option': 'SL', 'title': 'Sleeve' },
        { 'option': 'SO', 'title': 'Spool' },
        { 'option': '15', 'title': 'Stick' },
        { 'option': 'TH', 'title': 'Thousand' },
        { 'option': 'TY', 'title': 'Tray' },
        { 'option': 'TB', 'title': 'Tube' },
        { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
      'field_map': 'quantityPerApplication',
      'item_name': 'details.quantityPerApplication',
      'title': 'Quantity per application',
      'hint_detail': 'Typical quantity used on a single vehicle fitment or application.',
      'hint_panel' : 'Enter the typical quantity used on a single vehicle fitment or application.',
      'example': '1',
      'sequence': 11,
      'show_more': true,
      'numerical': true,
      'validation': {
        'PIES_6_5' : {
          max: 99999999,
          message: 'Quantity per application must be less than 99999999.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'quantityPerApplicationUom',
      'item_name': 'details.quantityPerApplicationUom',
      'title': 'Units of measure',
      'hint_detail': 'Unit of measure for the quantity used on a single vehicle fitment or application.',
      'hint_panel' : 'Enter the unit of measure for the quantity used on a single vehicle fitment or application.',
      'example': 'Each',
      'sequence': 12,
      'show_more': true,
      'lookup': [
        { 'option': '', 'title': '-' },
        { 'option': 'OZ', 'title': 'Ounces' },
        { 'option': 'AS', 'title': 'Assortment' },
        { 'option': 'BG', 'title': 'Bag' },
        { 'option': 'BK', 'title': 'Book' },
        { 'option': 'BO', 'title': 'Bottle' },
        { 'option': 'BX', 'title': 'Box' },
        { 'option': 'BC', 'title': 'Bucket' },
        { 'option': 'BD', 'title': 'Bundle' },
        { 'option': 'CN', 'title': 'Can' },
        { 'option': 'CG', 'title': 'Card' },
        { 'option': 'CT', 'title': 'Carton' },
        { 'option': 'CQ', 'title': 'Cartridge' },
        { 'option': 'CA', 'title': 'Case' },
        { 'option': 'CX', 'title': 'Coil' },
        { 'option': 'CH', 'title': 'Container' },
        { 'option': 'FN', 'title': 'Clamshell' },
        { 'option': 'CP', 'title': 'Crate' },
        { 'option': 'DZ', 'title': 'Dozen' },
        { 'option': 'DR', 'title': 'Drum' },
        { 'option': 'EA', 'title': 'Each' },
        { 'option': 'EV', 'title': 'Envelope' },
        { 'option': 'FT', 'title': 'Foot' },
        { 'option': 'GA', 'title': 'Gallon' },
        { 'option': 'HU', 'title': 'Hundred' },
        { 'option': 'HC', 'title': 'Hundred count' },
        { 'option': 'JR', 'title': 'Jar' },
        { 'option': 'KE', 'title': 'Keg' },
        { 'option': 'KG', 'title': 'Kilogram' },
        { 'option': 'KT', 'title': 'Kit' },
        { 'option': 'LT', 'title': 'Liter' },
        { 'option': 'MR', 'title': 'Meter' },
        { 'option': 'PH', 'title': 'Pack (PAK)' },
        { 'option': 'PK', 'title': 'Package' },
        { 'option': 'PA', 'title': 'Pail' },
        { 'option': 'PR', 'title': 'Pair' },
        { 'option': 'PF', 'title': 'Pallet' },
        { 'option': 'PL', 'title': 'Pallet unit load' },
        { 'option': 'PC', 'title': 'Piece' },
        { 'option': 'PT', 'title': 'Pint' },
        { 'option': 'LB', 'title': 'Pound' },
        { 'option': 'QT', 'title': 'Quart' },
        { 'option': 'RE', 'title': 'Reel' },
        { 'option': 'RD', 'title': 'Rod' },
        { 'option': 'RL', 'title': 'Roll' },
        { 'option': 'ST', 'title': 'Set' },
        { 'option': 'SH', 'title': 'Sheet' },
        { 'option': 'SL', 'title': 'Sleeve' },
        { 'option': 'SO', 'title': 'Spool' },
        { 'option': '15', 'title': 'Stick' },
        { 'option': 'TH', 'title': 'Thousand' },
        { 'option': 'TY', 'title': 'Tray' },
        { 'option': 'TB', 'title': 'Tube' },
        { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
      'field_map': 'quantityPerApplicationQualifier',
      'item_name': 'details.quantityPerApplicationQualifier',
      'title': 'Qualifier',
      'hint_detail': 'This is a user-defined qualifier for quantity per application. Maintenance is at the Global account level.',
      'hint_panel' : 'Select the qualifier for application quantity for this item on a single vehicle fitment or application. MAX - Maximum used in any Vehicle MIN - Minimum Quantity Used in a Vehicle NOR - Normal Quantity Used in a Vehicle REQ - As Required VAR - Variable - See ACES App Field V91 ZZ1-ZZ9 - User-defined qualifier values, maintained at the Global account level.',
      'example': 'As required',
      'sequence': 12,
      'show_more': true,
      'lookup': [
        {
          'option': '',
          'title': '-'
        },
        {
          'option': 'MAX',
          'title': 'Maximum used'
        },
        {
          'option': 'MIN',
          'title': 'Minimum used'
        },
        {
          'option': 'NOR',
          'title': 'Normal used'
        },
        {
          'option': 'NOT',
          'title': 'Not applicable to vehicles'
        },
        {
          'option': 'REQ',
          'title': 'As required'
        },
        {
          'option': 'VAR',
          'title': 'Variable - see ACES application field V91'
        },
        {
          'option': 'ZZ1',
          'title': 'User defined 1'
        },
        {
          'option': 'ZZ2',
          'title': 'User defined 2'
        },
        {
          'option': 'ZZ3',
          'title': 'User defined 3'
        },
        {
          'option': 'ZZ4',
          'title': 'User defined 4'
        },
        {
          'option': 'ZZ5',
          'title': 'User defined 5'
        },
        {
          'option': 'ZZ6',
          'title': 'User defined 6'
        },
        {
          'option': 'ZZ7',
          'title': 'User defined 7'
        },
        {
          'option': 'ZZ8',
          'title': 'User defined 8'
        },
        {
          'option': 'ZZ9',
          'title': 'User defined 9'
        }
      ]
    },
    {
      'field_map': 'hazardousMaterialCode',
      'item_name': 'details.hazardousMaterialCode',
      'title': 'Hazardous',
      'hint_detail': 'Yes or No if product contains hazardous material.',
      'hint_panel' : 'Select Yes or No if product contains hazardous material.',
      'example': 'No',
      'sequence': 13,
      'show_more': true,
      'lookup': [
        {
          'option': '',
          'title': '-'
        },
        {
          'option': 'N',
          'title': 'No'
        },
        {
          'option': 'Y',
          'title': 'Yes'
        }
      ]
    },
    {
      'field_map': 'taxable',
      'item_name': 'details.taxable',
      'title': 'Taxable',
      'hint_detail': 'Indicates whether this product is taxable or not.',
      'hint_panel' : 'Select whether this product is taxable or not.',
      'example': 'Yes',
      'sequence': 14,
      'show_more': true,
      'lookup': [
        {
          'option': '',
          'title': '-'
        },
        {
          'option': 'N',
          'title': 'No'
        },
        {
          'option': 'Y',
          'title': 'Yes'
        }
      ]
    },
    {
      'field_map': 'subBrand',
      'item_name': 'details.subBrand',
      'title': 'Sub Brand',
      'hint_detail': 'SubBrand name corresponding with SubBrand ID in the Auto Care maintained Parent/Supplier/Brand Code Registry.',
      'hint_panel' : 'SubBrand name corresponding with SubBrand ID in the Auto Care maintained Parent/Supplier/Brand Code Registry.',
      'example': 'ABC AM Products',
      'sequence': 15,
      'show_more': false,
      'lookup': null,
      'api_lookup': {
          'type': 'list',
          'lookup': 'sub-brands',
          'field_map': 'brandId'
      }
    },
    {
      'field_map': 'category',
      'item_name': 'details.category',
      'title': 'Category',
      'hint_detail': 'The product category code is maintained by the Auto Care Association Category Management Committee.',
      'hint_panel' : 'Enter the Product Category Code. Codes are available from the Auto Care Association Category Management Committee.',
      'example': '330102',
      'sequence': 16,
      'show_more': false,
      'validation': {
        'PIES_6_5' : {
          length: 6,
          message: "Category can be no more than 6 characters."
        }
      },
      'lookup': null
    },
    {
      'field_map': 'effectiveDate',
      'item_name': 'details.effectiveDate',
      'title': 'Effective date',
      'hint_detail': 'Date the Item is available for purchase at the current price levels (MM/DD/YYYY)',
      'hint_panel' : 'Date the Item is available for purchase at the current price levels (MM/DD/YYYY)',
      'example': '01/03/2009',
      'sequence': 16,
      'show_more': false,
      'lookup': null,
      'validation': {
        'PIES_6_5': {
          message: 'Effective date cannot be set ahead of Available date.',
        }
      }
    },
    {
      'field_map': 'availableDate',
      'item_name': 'details.availableDate',
      'title': 'Available date',
      'hint_detail': 'Date the Item is available for Sale (MM/DD/YYYY)',
      'hint_panel' : 'Date the Item is available for Sale (MM/DD/YYYY)',
      'example': '01/03/2010',
      'sequence': 16,
      'show_more': false,
      'lookup': null
    },
    {
      'field_map': 'emissionCode',
      'item_name': 'details.emissionCode',
      'title': 'Emission code',
      'hint_detail': 'Indicates if this product is legal for use in the U.S. including California, off-road only, or legal for use in U.S. excluding California.',
      'hint_panel' : 'Select if this product is legal for use in the U.S. including California, off-road only, or legal for use in U.S. excluding California.',
      'example': 'Legal in US',
      'sequence': 17,
      'show_more': false,
      'lookup': [
        {
          'option': '1',
          'title': 'CARB Eligible'
        },
        {
          'option': '2',
          'title': 'EPA Eligible'
        },
        {
          'option': '3',
          'title': 'Racing or Pre-emissions use only'
        },
        {
          'option': '4',
          'title': 'Emissions Compliance Exempt'
        },
        {
          'option': '5',
          'title': 'Emissions Status Pending'
        }
      ]
    },
    {
      'field_map': 'basePartNumber',
      'item_name': 'details.basePartNumber',
      'title': 'Base part no.',
      'hint_detail': 'Link this to another part number if this part is a configuration or option., like 123-BLK, 123-RED, etc.',
      'hint_panel' : 'Link this to another part number if this part is a configuration or option., like 123-BLK, 123-RED, etc.',
      'example': 'My base part number is HS123, which comes in several color options, like BLK (black), RED (red), etc.',
      'sequence': 17,
      'show_more': false,
      'validation': {
        'PIES_6_5' : {
          minLength: 1,
          maxLength: 48,
          pattern: patterns.alphaNumericalDashes,
          message: 'Base part no. cannot be longer than 48 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'unspscCode',
      'item_name': 'details.unspscCode',
      'title': 'UNSPSC code',
      'hint_detail': 'Maintained by GS1, the United Nations Standard Products and Services Code (UNSPSC) is numeric value for use in eCommerce.',
      'hint_panel' : 'Enter the appropriate United Nations Standard Products and Services Code (UNSPSC) for this item.',
      'example': '11223344',
      'sequence': 20,
      'show_more': false,
      'validation': {
        'PIES_6_5' : {
          minLength: 8,
          maxLength: 10,
          message: 'UNSPSC code must be between 8 and 10 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'vmrsCode',
      'item_name': 'details.vmrsCode',
      'title': 'VMRS code',
      'hint_detail': 'The Vehicle Maintenance Reporting Standard description (VMRS) is part of the American Trucking Associations structured coding system.',
      'hint_panel' : 'The Vehicle Maintenance Reporting Standard description (VMRS) is part of the American Trucking Associations structured coding system.',
      'example': '10456789',
      'sequence': 21,
      'show_more': false,
      'validation': {
        'PIES_6_5' : {
          length: 9,
          message: 'VMRS code must be exactly 9 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'coreId',
      'item_name': 'details.core.id',
      'title': 'Core class',
      'hint_detail': 'Indicates which Core Class for core returns this item belongs in. The Core Class is maintained at the Global account level.',
      'hint_panel' : 'Select the Core Class for core returns this item belongs in. The Core Class is defined and maintained at the Global account level.',
      'example': 'CC100',
      'sequence': 22,
      'show_more': false,
      'lookup': null // dynamical loaded
    },
    {
      'field_map': 'coreGroup',
      'item_name': 'details.core.group',
      'title': 'Core group',
      'hint_detail': 'Indicates which Core Group for core returns this item belongs in. The Core Group is maintained at the Global account level.',
      'hint_panel' : 'Indicates which Core Group for core returns this item belongs in. The Core Group is maintained at the Global account level.',
      'example': 'CG100',
      'sequence': 23,
      'show_more': false,
      'lookup': null
    },
    {
      'field_map': 'coreReturnDaysToExpiry',
      'item_name': 'details.core.days',
      'title': 'Core return days to expiry',
      'hint_detail': 'Indicates the days within which the core can be returned without penalty. Days of Expiry is maintained at the Global account level.',
      'hint_panel' : 'Indicates the days within which the core can be returned without penalty. Days of Expiry is maintained at the Global account level.',
      'example': '30 days',
      'sequence': 24,
      'show_more': false,
      'lookup': null
    },
    {
      'field_map': 'corePartNumber',
      'item_name': 'details.core.part_number',
      'title': 'Core part no.',
      'hint_detail': 'The specific part number for this item core.',
      'hint_panel' : 'Enter the core part number for this item.',
      'example': 'ABC5690',
      'sequence': 25,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 48,
          'message': 'Core part no. cannot be longer than 48 characters.'
        }
      },
      'lookup': null,
    },
    {
      'field_map': 'returnCode',
      'item_name': 'details.returnCode',
      'title': 'Return code',
      'hint_detail': 'Indicates the return code for the core part number.',
      'hint_panel' : 'Enter up to 3 characters as the return code for the core part number.',
      'example': 'RC1',
      'sequence': 26,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 3,
          'message': 'Return code cannot be longer than 3 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'warrantyDistance',
      'item_name': 'details.warrantyDistance',
      'title': 'Warranty distance',
      'hint_detail': 'If applicable, indicates the distance limit covered for this item under warranty.',
      'hint_panel' : 'If applicable, enter the distance limit in which this item warranty is effective.',
      'example': '3,000',
      'sequence': 27,
      'show_more': false,
      'numerical': true,
      'validation' : {
        'PIES_6_5': {
          'max': 9999999,
          'message': 'Warranty distance must be less than 9999999 (9,999,999)'
        }
      },
      'lookup': null
    },{
      'field_map': 'warrantyDistanceUom',
      'item_name': 'details.warrantyDistanceUom',
      'title': 'Distance units of measure',
      'hint_detail': 'Distance units of measure',
      'hint_panel' : 'Distance units of measure',
      'example': "Miles",
      'sequence': 28,
      'show_more': false,
      'lookup':  [
        {
          'option': '',
          'title': '-'
        },
        {
          'option': 'MI',
          'title': 'Miles'
        }
      ]
    },
    {
      'field_map': 'warrantySpecial',
      'item_name': 'details.warrantySpecial',
      'title': 'Warranty special',
      'hint_detail': 'If applicable, indicates the special warranty condition for this item.',
      'hint_panel' : 'If applicable, enter the special warranty condition for this item.',
      'example': 'Lifetime',
      'sequence': 29,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 256,
          'message': 'Warranty special cannot be longer than 256 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'warrantySpecialUom',
      'item_name': 'details.warrantySpecialUom',
      'title': 'Special units of measure',
      'hint_detail': 'If applicable, indicates units of measure for the special warranty condition for this item.',
      'hint_panel' : 'If applicable, select the units of measure for the special warranty condition for this item.',
      'example': 'Lifetime',
      'sequence': 30,
      'show_more': false,
      'lookup':  [
        {
          'option': '',
          'title': '-'
        },
        {
          'option': 'LF',
          'title': 'Lifetime'
        },
        {
          'option': 'SR',
          'title': 'See Rep'
        },
        {
          'option': 'TX',
          'title': 'Text'
        }
      ]
    },
    {
      'field_map': 'warrantyTime',
      'item_name': 'details.warrantyTime',
      'title': 'Warranty time',
      'hint_detail': 'If applicable, indicates the time limit covered for this item under warranty.',
      'hint_panel' : 'If applicable, enter the time limit covered for this item under warranty.',
      'example': '24',
      'sequence': 31,
      'show_more': false,
      'numerical': true,
      'validation' : {
        'PIES_6_5': {
          'max': 9999,
          'message': 'Warranty time cannot be longer than 9999 (9,999)'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'warrantyTimeUom',
      'item_name': 'details.warrantyTimeUom',
      'title': 'Time units of measure',
      'hint_detail': 'If applicable, indicates the units of measure for the time limit covered for this item under warranty.',
      'hint_panel' : 'If applicable, select the units of measure for the time limit covered for this item under warranty.',
      'example': 'Months',
      'sequence': 32,
      'show_more': false,
      'lookup': [
        {
          'option': '',
          'title': '-'
        },
        {
          'option': 'DA',
          'title': 'Days'
        },
        {
          'option': 'WK',
          'title': 'Weeks'
        },
        {
          'option': 'MO',
          'title': 'Months'
        },
        {
          'option': 'YR',
          'title': 'Years'
        }
      ]
    },
    {
      'field_map': 'refurbishedPart',
      'item_name': 'details.refurbishedPart',
      'title': 'Refurbished part',
      'hint_detail': 'Indicates whether this item is refurbished or not.',
      'hint_panel' : 'Select whether this item is refurbished or not.',
      'example': 'Yes',
      'sequence': 33,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 1,
          'message': 'Please select YES if this item is refurbished or NO not'
        }
      },
      'lookup': [
        {
          'option': '',
          'title': '-'
        },
        {
          'option' : 'N',
          'title' : 'No'
        },
        {
          'option' : 'Y',
          'title' : 'Yes'
        }
      ]
    },
    {
      'field_map': 'remanufacturedPart',
      'item_name': 'details.remanufacturedPart',
      'title': 'Remanufactured part',
      'hint_detail': 'Indicates whether this item is remanufactured or not.',
      'hint_panel' : 'Select whether this item is remanufactured or not.',
      'example': 'Yes',
      'sequence': 34,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 1,
          'message': 'Please select YES if this item remanufactured or NO if this item is universal'
        }
      },
      'lookup': [
        {
          'option': '',
          'title': '-'
        },
        {
          'option' : 'N',
          'title' : 'No'
        },
        {
          'option' : 'Y',
          'title' : 'Yes'
        }
      ]
    },
    {
      'field_map': 'oemPartNumber',
      'item_name': 'details.oemPartNumber',
      'title': 'OEM Part no.',
      'hint_detail': 'If applicable, indicates the Original Equipment Manufacturer (OEM) part number for this item.',
      'hint_panel' : 'If applicable, enter the Original Equipment Manufacturer (OEM) part number for this item.',
      'example': '8A4567',
      'sequence': 35,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 48,
          'pattern': patterns.ANpartNumber,
          'message': 'OEM part no. cannot be longer than 48 characters. No special characters allowed: ;~[]{}|,""%!$*\\^?\'"'
        }
      },
      'lookup': null,
      'not_bulk_editable': true,
    },
    {
      'field_map': 'oemBrand',
      'item_name': 'details.oemBrand',
      'title': 'OEM brand',
      'hint_detail': 'The name of the Original Equipment Manufacturer (OEM).',
      'hint_panel' : 'If applicable, enter the Original Equipment Manufacturer (OEM) part number for this item.',
      'example': 'Z Car Motors',
      'sequence': 36,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 35,
          'message': 'OEM Brand cannot be longer than 35 characters'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'originalSupplierPartNumber',
      'item_name': 'details.originalSupplierPartNumber',
      'title': 'Original supplier part number',
      'hint_detail': 'If applicable, indicates the Original Supplier part number for this item.',
      'hint_panel' : 'If applicable, enter the Original Supplier part number for this item.',
      'example': '18A4567',
      'sequence': 37,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 48,
          'pattern': patterns.ANpartNumber,
          'message': 'Original supplier part no. cannot be longer than 48 characters. No special characters allowed: ;[]{}~|,""%!$*\\^?\'"'
        }
      },
      'lookup': null,
      'not_bulk_editable': true,
    },
    {
      'field_map': 'originalSupplierName',
      'type': 'list',
      'item_name': 'details.originalSupplierName',
      'title': 'Original supplier name',
      'hint_detail': 'The name of the original supplier.',
      'hint_panel' : 'Enter the name of the original supplier.',
      'example': 'Standard Parts',
      'sequence': 38,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 35,
          'message': 'Original supplier name cannot be longer than 35 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'oldPartNumber',
      'type': 'list',
      'item_name': 'details.oldPartNumber',
      'title': 'Old part no.',
      'hint_detail': 'The old part number for this item.',
      'hint_panel' : 'Enter the old part number for this item.',
      'example': 'HOS9988',
      'sequence': 39,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 48,
          'pattern': patterns.ANpartNumber,
          'message': 'Old part no. cannot be longer than 48 characters. No special characters allowed: ;~[]{}|,""%!$*\\^?\'"'
        }
      },
      'lookup': null,
      'not_bulk_editable': true,
    },
    {
      'field_map': 'partNumberSupersededTo',
      'type': 'list',
      'item_name': 'details.partNumberSupersededTo',
      'title': 'Part no. superseded to',
      'hint_detail': 'The superseded to part number for this item.',
      'hint_panel' : 'Enter the superseded to part number for this item.',
      'example': 'HOS-98765',
      'sequence': 40,
      'show_more': false,
      'validation' : {
        'PIES_6_5': {
          'minLength': 1,
          'maxLength': 48,
          'pattern': patterns.ANpartNumber,
          'message': 'Part no. superseded to cannot be longer than 48 characters. No special characters allowed: ;[]{}~|,""%!$*\\^?\'"'
        }
      },
      'lookup': null,
      'not_bulk_editable': true,
    }
  ],
  "descriptions": descriptionSegmentData,
  "attributes": [
    // only for PAdb
    {
        "field_map": "assignment_id",
        "item_name": "assignment_id",
        "title": "Attribute assignment ID",
        "sequence": 1,
    },
    {
        "field_map": "attribute_id",
        "item_name": "attribute_id",
        "title": "Attribute ID",
        "sequence": 1,
    },
    {
        "field_map": "name",
        "item_name": "name",
        "title": "Attribute",
        "hint_detail": "Either industry-defined attributes from the Product Attribute Database (PAdb), or user-defined attributes. Delivered in the sequence shown here.",
        "hint_panel" : "Either industry-defined attributes from the Product Attribute Database (PAdb), or user-defined attributes. Delivered in the sequence shown here.",
        "example": "Material",
        "sequence": 1,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5': {
            required: true,
            minLength: 1,
            maxLength: 80,
            message: "Attribute name cannot be longer than 80 characters."
          }
        }
    },
    {
        "field_map": "value",
        "item_name": "value",
        "title": "Value",
        "type": "list",
        "hint_detail": "Value of the attribute",
        "hint_panel" : "Value of the attribute. If the attribute value is a measurement of some type (e.g., weight, diameter, thickness, etc.) do not combine the unit of measure with the value like '1.5 in.' Please use the Units to define the unit of measure.",
        "example": "Aluminum",
        "sequence": 2,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5': {
            required: true,
            minLength: 1,
            maxLength: 2000,
            message: "Attribute value cannot be longer than 2000 characters."
          }
        }
    },
    {
        "field_map": "is_padb",
        "item_name": "is_padb",
        "title": "Is PAdb",
        "sequence": 2,
        "show_more": true,
        "lookup": null,
    },
    {
        "field_map": "uom_label",
        "item_name": "uom_label",
        "title": "Units",
        "hint_detail": "Unit of measure",
        "hint_panel" : "Select a standard PAdb attribute and standard unit of measure.",
        "example": "",
        "sequence": 3,
        "show_more": true,
        "lookup": null,
    },
    {
        "field_map": "uom",
        "item_name": "uom",
        "title": "Units",
        "hint_detail": "Unit of measure",
        "hint_panel" : "Select a standard PAdb attribute and standard unit of measure.",
        "example": "",
        "sequence": 3,
        "show_more": true,
        "lookup": null,
    },
    {
        "field_map": "uom_description",
        "item_name": "uom_description",
        "title": "Units",
        "hint_detail": "Unit of measure",
        "hint_panel" : "Select a standard PAdb attribute and standard unit of measure.",
        "example": "",
        "sequence": 3,
        "show_more": true,
        "lookup": null,
    },
    // only for PAdb
    {
        "field_map": "style_id",
        "item_name": "style_id",
        "title": "Style",
        "hint_detail": "Style can represent a variation in the construction of a part or different performance characteristics.",
        "hint_panel" : "Select a standard style which can represent a variation in the construction of a part or different performance characteristics.",
        "example": "",
        "sequence": 5,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            required: true,
            maxLength: 48
          }
        }
    },
    {
        "field_map": "style_name",
        "item_name": "style_name",
        "title": "Style",
        "hint_detail": "Style can represent a variation in the construction of a part or different performance characteristics.",
        "hint_panel" : "Select a standard style which can represent a variation in the construction of a part or different performance characteristics.",
        "example": "",
        "sequence": 6,
        "show_more": true,
        "lookup": null,
    },
    {
      "field_map": "description",
      "item_name": "description",
      "title": "Description",
      "example": "Description",
      "sequence": 1,
      "show_more": true,
      "lookup": null,
    },
    // only for PAdb
    {
      "field_map": "valid_attribute_values",
      "item_name": "valid_attribute_values",
      "title": "Valid attribute values",
      "sequence": 1,
      "show_more": true,
      "lookup": null,
    },
    // only for PAdb
    {
      "field_map": "meta_uom_codes",
      "item_name": "meta_uom_codes",
      "title": "Meta uom codes",
      "sequence": 1,
      "show_more": true,
      "lookup": null,
    },
    // only for PAdb
    {
      "field_map": "styles",
      "item_name": "styles",
      "title": "Styles",
      "sequence": 1,
      "show_more": true,
      "lookup": null,
    },
    // only for PAdb
    {
      "field_map": "meta_data",
      "item_name": "meta_data",
      "title": "Meta data",
      "sequence": 1,
      "show_more": true,
      "lookup": null,
    },
  ],
  "prices": [
    {
        "item_name": "JBR",
        "title": "Jobber",
        "hint_detail": "An industry standard price point used to determine wholesale prices. This price is often lower than retail but higher than wholesaler price.",
        "hint_panel" : "An industry standard price point used to determine wholesale prices. This price is often lower than retail but higher than wholesaler price.",
        "example": "10.15",
        "sequence": 1,
        "show_more": true,
        "lookup": true
    },
    {
        "item_name": "RET",
        "title": "Retail",
        "hint_detail": "Suggested price to the general public.",
        "hint_panel" : "Suggested price to the general public.",
        "example": "",
        "sequence": 2,
        "show_more": true,
        "lookup": true
    },
    {
        "item_name": "RMP",
        "title": "Retail MAP",
        "hint_detail": "Minimum Advertised Price to the general public. This is not the same as unilateral price.",
        "hint_panel" : "Minimum Advertised Price to the general public. This is not the same as unilateral price.",
        "example": "",
        "sequence": 3,
        "show_more": true,
        "lookup": true
    },
    {
        "item_name": "WD1",
        "title": "Distributor",
        "hint_detail": "Distributor price is the same as the wholesaler or trade price.",
        "hint_panel" : "Distributor price is the same as the wholesaler or trade price.",
        "example": "",
        "sequence": 4,
        "show_more": true,
        "lookup": true
    },
    {
        "item_name": "WMP",
        "title": "Wholesale MAP",
        "hint_detail": "Minimum advertised price for wholesale customers.",
        "hint_panel" : "Minimum advertised price for wholesale customers.",
        "example": "",
        "sequence": 5,
        "show_more": true,
        "lookup": true
    },
    {
        "item_name": "AC1",
        "title": "Special additional cost 1",
        "hint_detail": "Additional costs including handling, freight charges, and other charges specific to the part.",
        "hint_panel" : "Additional costs including handling, freight charges, and other charges specific to the part.",
        "example": "",
        "sequence": 6,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "AC2",
        "title": "Special additional cost 1",
        "hint_detail": "Additional costs including handling, freight charges, and other charges specific to the part.",
        "hint_panel" : "Additional costs including handling, freight charges, and other charges specific to the part.",
        "example": "",
        "sequence": 7,
        "show_more": false,
        "lookup": true
    },
    {
      "item_name": "CWD",
      "title": "WD Cost",
      "hint_detail": "Warehouse distributor cost",
      "hint_panel" : "Warehouse distributor cost",
      "example": "",
      "sequence": 8,
      "show_more": false,
      "lookup": true
    },
    {
        "item_name": "CR1",
        "title": "Core 1",
        "hint_detail": "Core 1 price",
        "hint_panel" : "Core 1 price",
        "example": "",
        "sequence": 9,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "CR2",
        "title": "Core 2",
        "hint_detail": "Core 2 price",
        "hint_panel" : "Core 2 price",
        "example": "",
        "sequence": 10,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "CR3",
        "title": "Core 3",
        "hint_detail": "Core 3 price",
        "hint_panel" : "Core 3 price",
        "example": "",
        "sequence": 11,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "CR4",
        "title": "Core 4",
        "hint_detail": "Core 4 price",
        "hint_panel" : "Core 4 price",
        "example": "",
        "sequence": 12,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "CRG",
        "title": "Good core",
        "hint_detail": "The sale of a remanufactured part involves a core price to be refunded to encourage the return of the old part for reconditioning purposes.",
        "hint_panel" : "The sale of a remanufactured part involves a core price to be refunded to encourage the return of the old part for reconditioning purposes.",
        "example": "",
        "sequence": 13,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "CRJ",
        "title": "Jobber core",
        "hint_detail": "Core price for jobbers.",
        "hint_panel" : "Core price for jobbers.",
        "example": "",
        "sequence": 14,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "CRW",
        "title": "WD core",
        "hint_detail": "Core price for warehouse distributors.",
        "hint_panel" : "Core price for warehouse distributors.",
        "example": "",
        "sequence": 15,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "DLN",
        "title": "Non-stock dealer",
        "hint_detail": "Non-stocking or drop shipping dealer.",
        "hint_panel" : "Non-stocking or drop shipping dealer.",
        "example": "",
        "sequence": 16,
        "show_more": false,
        "lookup": true
    },

    {
        "item_name": "DLR",
        "title": "Dealer",
        "hint_detail": "A wholesale price which can be the same or higher than Jobber price and lower than retail price.",
        "hint_panel" : "A wholesale price which can be the same or higher than Jobber price and lower than retail price.",
        "example": "",
        "sequence": 17,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "DLV",
        "title": "Volume dealer",
        "hint_detail": "A wholesale price for higher volumes which can be the same or higher than Jobber price and lower than retail price.",
        "hint_panel" : "A wholesale price for higher volumes which can be the same or higher than Jobber price and lower than retail price.",
        "example": "",
        "sequence": 18,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "FLT",
        "title": "Fleet",
        "hint_detail": "A special discount price for the purchase of multiple items used in a vehicle fleet.",
        "hint_panel" : "SA special discount price for the purchase of multiple items used in a vehicle fleet.",
        "example": "",
        "sequence": 19,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "INM",
        "title": "Master installer",
        "hint_detail": "Master installer",
        "hint_panel" : "Master installer",
        "example": "",
        "sequence": 20,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "INN",
        "title": "NS installer",
        "hint_detail": "NS installer",
        "hint_panel" : "NS installer",
        "example": "",
        "sequence": 21,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "INP",
        "title": "Installer preferred",
        "hint_detail": "Installer preferred",
        "hint_panel" : "Installer preferred",
        "example": "",
        "sequence": 22,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "INS",
        "title": "Stocking installer",
        "hint_detail": "Stocking installer",
        "hint_panel" : "Stocking installer",
        "example": "",
        "sequence": 23,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "JBC",
        "title": "Conf-jobber",
        "hint_detail": "Conf-jobber",
        "hint_panel" : "Conf-jobber",
        "example": "",
        "sequence": 24,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "JBN",
        "title": "NS jobber",
        "hint_detail": "NS jobber",
        "hint_panel" : "NS jobber",
        "example": "",
        "sequence": 25,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "LST",
        "title": "List",
        "hint_detail": "List price is the same as the suggested retail price.",
        "hint_panel" : "List price is the same as the suggested retail price.",
        "example": "",
        "sequence": 26,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "MEC",
        "title": "Mechanic",
        "hint_detail": "Details hint - mechanic",
        "hint_panel" : "",
        "example": "",
        "sequence": 27,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "PKP",
        "title": "Pickup",
        "hint_detail": "Price when product is picked up by customer, free of freight burden.",
        "hint_panel" : "Price when product is picked up by customer, free of freight burden.",
        "example": "",
        "sequence": 28,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "PRO",
        "title": "Special promo",
        "hint_detail": "Lower price to get attention, or move excess inventory.",
        "hint_panel" : "Lower price to get attention, or move excess inventory.",
        "example": "",
        "sequence": 29,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "QOT",
        "title": "Quote",
        "hint_detail": "Quote",
        "hint_panel" : "Quote",
        "example": "",
        "sequence": 30,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "RAC",
        "title": "Racer",
        "hint_detail": "Wholesale price given to professional or amateur racers. This price is often lower than retail but higher than jobber.",
        "hint_panel" : "Wholesale price given to professional or amateur racers. This price is often lower than retail but higher than jobber.",
        "example": "",
        "sequence": 31,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "TRD",
        "title": "Trade",
        "hint_detail": "Trade price is the same as the wholesaler or distributor price.",
        "hint_panel" : "Trade price is the same as the wholesaler or distributor price.",
        "example": "",
        "sequence": 32,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "UNL",
        "title": "Unilateral price",
        "hint_detail": "The minimum sales price to the general public, including any discounts retailer may offer.",
        "hint_panel" : "The minimum sales price to the general public, including any discounts retailer may offer.",
        "example": "",
        "sequence": 33,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "WLS",
        "title": "Wholesale",
        "hint_detail": "Price for an industry or trade buyer. This price is often lower than jobber price.",
        "hint_panel" : "Price for an industry or trade buyer. This price is often lower than jobber price.",
        "example": "",
        "sequence": 34,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ1",
        "title": "User defined 1",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 35,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ2",
        "title": "User defined 2",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 36,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ3",
        "title": "User defined 3",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 37,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ4",
        "title": "User defined 4",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 38,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ5",
        "title": "User defined 5",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 39,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ6",
        "title": "User defined 6",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 40,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ7",
        "title": "User defined 7",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 41,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ8",
        "title": "User defined 8",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 42,
        "show_more": false,
        "lookup": true
    },
    {
        "item_name": "ZZ9",
        "title": "User defined 9",
        "hint_detail": "User defined",
        "hint_panel" : "User defined",
        "example": "",
        "sequence": 43,
        "show_more": false,
        "lookup": true
    },
  ],
  "packages": [
    {
        "field_map": "title",
        "item_name": "EA.title",
        "title": "Package type",
        "hint_detail": "Smallest pack (consumer level)",
        "hint_panel" : "Smallest pack (consumer level)",
        "example": "",
        "sequence": 1,
        "show_more": true,
        "lookup": null
    },
    {
        "field_map": "title",
        "item_name": "PK.title",
        "title": "Package type",
        "hint_detail": "The shippable inner package.",
        "hint_panel" : "The shippable inner package.",
        "example": "",
        "sequence": 1,
        "show_more": true,
        "lookup": null
    },
    {
        "field_map": "title",
        "item_name": "BX.title",
        "title": "Package type",
        "hint_detail": "Inner pack (non-shippable)",
        "hint_panel" : "Inner pack (non-shippable)",
        "example": "",
        "sequence": 1,
        "show_more": true,
        "lookup": null
    },
    {
        "field_map": "title",
        "item_name": "CA.title",
        "title": "Package type",
        "hint_detail": "Case",
        "hint_panel" : "Case",
        "example": "",
        "sequence": 1,
        "show_more": true,
        "lookup": null
    },
    {
        "field_map": "title",
        "item_name": "PL.title",
        "title": "Package type",
        "hint_detail": "Pallet",
        "hint_panel" : "Pallet",
        "example": "",
        "sequence": 1,
        "show_more": true,
        "lookup": null
    },
    {
        "field_map": "orderable",
        "item_name": "EA.orderable",
        "title": "Orderable",
        "hint_detail": "YES or NO if this package is orderable.",
        "hint_panel" : "YES or NO if this package is orderable.",
        "example": "",
        "sequence": 2,
        "show_more": true,
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "N",
            "title": "No",
          },
          {
            "option": "Y",
            "title": "Yes",
          }
        ]
    },
    {
        "field_map": "orderable",
        "item_name": "PK.orderable",
        "title": "Orderable",
        "hint_detail": "YES or NO if this package is orderable.",
        "hint_panel" : "YES or NO if this package is orderable.",
        "example": "",
        "sequence": 2,
        "show_more": true,
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "N",
            "title": "No",
          },
          {
            "option": "Y",
            "title": "Yes",
          }
        ]
    },
    {
        "field_map": "orderable",
        "item_name": "BX.orderable",
        "title": "Orderable",
        "hint_detail": "YES or NO if this package is orderable.",
        "hint_panel" : "YES or NO if this package is orderable.",
        "example": "",
        "sequence": 2,
        "show_more": true,
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "N",
            "title": "No",
          },
          {
            "option": "Y",
            "title": "Yes",
          }
        ]
    },
    {
        "field_map": "orderable",
        "item_name": "CA.orderable",
        "title": "Orderable",
        "hint_detail": "YES or NO if this package is orderable.",
        "hint_panel" : "YES or NO if this package is orderable.",
        "example": "",
        "sequence": 2,
        "show_more": true,
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "N",
            "title": "No",
          },
          {
            "option": "Y",
            "title": "Yes",
          }
        ]
    },
    {
        "field_map": "orderable",
        "item_name": "PL.orderable",
        "title": "Orderable",
        "hint_detail": "YES or NO if this package is orderable.",
        "hint_panel" : "YES or NO if this package is orderable.",
        "example": "",
        "sequence": 2,
        "show_more": true,
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "N",
            "title": "No",
          },
          {
            "option": "Y",
            "title": "Yes",
          }
        ]
    },
    {
        "field_map": "quantity",
        "item_name": "EA.quantity",
        "title": "Each per package",
        "hint_detail": "Number of items per package",
        "hint_panel" : "Number of items per package",
        "example": "1",
        "sequence": 3,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            max: 99999999,
            message: 'Minimum quantity must be less then 99999999.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "quantity",
        "item_name": "PK.quantity",
        "title": "Each per package",
        "hint_detail": "Number of items per package",
        "hint_panel" : "Number of items per package",
        "example": "1",
        "sequence": 3,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            max: 99999999,
            message: 'Minimum quantity must be less then 99999999.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "quantity",
        "item_name": "BX.quantity",
        "title": "Each per package",
        "hint_detail": "Number of items per package",
        "hint_panel" : "Number of items per package",
        "example": "1",
        "sequence": 3,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            max: 99999999,
            message: 'Minimum quantity must be less then 99999999.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "quantity",
        "item_name": "CA.quantity",
        "title": "Each per package",
        "hint_detail": "Number of items per package",
        "hint_panel" : "Number of items per package",
        "example": "3",
        "sequence": 3,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            max: 99999999,
            message: 'Minimum quantity must be less then 99999999.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "quantity",
        "item_name": "PL.quantity",
        "title": "Each per package",
        "hint_detail": "Number of items per package",
        "hint_panel" : "Number of items per package",
        "example": "4",
        "sequence": 3,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            max: 99999999,
            message: 'Minimum quantity must be less then 99999999.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsLength",
        "item_name": "EA.dimensions.length",
        "title": "Length",
        "hint_detail": "Length of the package with the product on its natural base and the graphics reading from left to right from the front.",
        "hint_panel" : "Length (also depth) is the front-to-back Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "7.775",
        "sequence": 4,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Length must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsLength",
        "item_name": "PK.dimensions.length",
        "title": "Length",
        "hint_detail": "Length of the package with the product on its natural base and the graphics reading from left to right from the front.",
        "hint_panel" : "Length (also depth) is the front-to-back Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "7.775",
        "sequence": 4,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Length must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsLength",
        "item_name": "BX.dimensions.length",
        "title": "Length",
        "hint_detail": "Length of the package with the product on its natural base and the graphics reading from left to right from the front.",
        "hint_panel" : "Length (also depth) is the front-to-back Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "7.775",
        "sequence": 4,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Length must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsLength",
        "item_name": "CA.dimensions.length",
        "title": "Length",
        "hint_detail": "Length of the package with the product on its natural base and the graphics reading from left to right from the front.",
        "hint_panel" : "Length (also depth) is the front-to-back Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "7.775",
        "sequence": 4,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Length must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsLength",
        "item_name": "PL.dimensions.length",
        "title": "Length",
        "hint_detail": "Length of the package with the product on its natural base and the graphics reading from left to right from the front.",
        "hint_panel" : "Length (also depth) is the front-to-back Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "7.775",
        "sequence": 4,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Length must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsWidth",
        "item_name": "EA.dimensions.width",
        "title": "Width",
        "hint_detail": "Width of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Width is the left-to-right Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "1.725",
        "sequence": 5,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Width must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsWidth",
        "item_name": "PK.dimensions.width",
        "title": "Width",
        "hint_detail": "Width of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Width is the left-to-right Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "1.725",
        "sequence": 5,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Width must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsWidth",
        "item_name": "BX.dimensions.width",
        "title": "Width",
        "hint_detail": "Width of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Width is the left-to-right Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "1.725",
        "sequence": 5,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Width must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsWidth",
        "item_name": "CA.dimensions.width",
        "title": "Width",
        "hint_detail": "Width of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Width is the left-to-right Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "1.725",
        "sequence": 5,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Width must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsWidth",
        "item_name": "PL.dimensions.width",
        "title": "Width",
        "hint_detail": "Width of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Width is the left-to-right Horizontal dimension. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "1.725",
        "sequence": 5,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Width must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsHeight",
        "item_name": "EA.dimensions.height",
        "title": "Height",
        "hint_detail": "Height of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Height = base to top. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "12.225",
        "sequence": 6,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Height must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsHeight",
        "item_name": "PK.dimensions.height",
        "title": "Height",
        "hint_detail": "Height of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Height = base to top. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "12.225",
        "sequence": 6,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Height must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsHeight",
        "item_name": "BX.dimensions.height",
        "title": "Height",
        "hint_detail": "Height of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Height = base to top. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "12.225",
        "sequence": 6,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Height must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsHeight",
        "item_name": "CA.dimensions.height",
        "title": "Height",
        "hint_detail": "Height of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Height = base to top. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "12.225",
        "sequence": 6,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Height must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsHeight",
        "item_name": "PL.dimensions.height",
        "title": "Height",
        "hint_detail": "Height of the package with the product on its natural base, with the graphics reading from left to right from the front.",
        "hint_panel" : "Height = base to top. Put the product on its natural base, with the graphics reading from left to right from the front. Note: This relates to how the product is merchandised, and it is possible it is packed for distribution in a different orientation.",
        "example": "12.225",
        "sequence": 6,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: 'Height must be greater than 0 and less than 8 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionsUom",
        "item_name": "EA.dimensions.uom",
        "title": "Dimensions unit measurement",
        "hint_detail": "Units of measurement for package dimensions. Inches and centimeters are the only two options. ",
        "hint_panel" : "Choose inches or centimeters as the unit of measurement for package dimensions. These are the only two available options. ",
        "example": "inches",
        "sequence": 7,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Dimensions unit of measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "IN",
            "title": "inches"
          },
          {
            "option": "CM",
            "title": "centimeters"
          }
        ]
    },
    {
        "field_map": "dimensionsUom",
        "item_name": "PK.dimensions.uom",
        "title": "Dimensions unit measurement",
        "hint_detail": "Units of measurement for package dimensions. Inches and centimeters are the only two options. ",
        "hint_panel" : "Choose inches or centimeters as the unit of measurement for package dimensions. These are the only two available options. ",
        "example": "inches",
        "sequence": 7,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Dimensions unit of measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "IN",
            "title": "inches"
          },
          {
            "option": "CM",
            "title": "centimeters"
          }
        ]
    },
    {
        "field_map": "dimensionsUom",
        "item_name": "BX.dimensions.uom",
        "title": "Dimensions unit measurement",
        "hint_detail": "Units of measurement for package dimensions. Inches and centimeters are the only two options. ",
        "hint_panel" : "Choose inches or centimeters as the unit of measurement for package dimensions. These are the only two available options. ",
        "example": "inches",
        "sequence": 7,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Dimensions unit of measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "IN",
            "title": "inches"
          },
          {
            "option": "CM",
            "title": "centimeters"
          }
        ]
    },
    {
        "field_map": "dimensionsUom",
        "item_name": "CA.dimensions.uom",
        "title": "Dimensions unit measurement",
        "hint_detail": "Units of measurement for package dimensions. Inches and centimeters are the only two options. ",
        "hint_panel" : "Choose inches or centimeters as the unit of measurement for package dimensions. These are the only two available options. ",
        "example": "inches",
        "sequence": 7,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Dimensions unit of measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "IN",
            "title": "inches"
          },
          {
            "option": "CM",
            "title": "centimeters"
          }
        ]
    },
    {
        "field_map": "dimensionsUom",
        "item_name": "PL.dimensions.uom",
        "title": "Dimensions unit measurement",
        "hint_detail": "Units of measurement for package dimensions. Inches and centimeters are the only two options. ",
        "hint_panel" : "Choose inches or centimeters as the unit of measurement for package dimensions. These are the only two available options. ",
        "example": "inches",
        "sequence": 7,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Dimensions unit of measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "IN",
            "title": "inches"
          },
          {
            "option": "CM",
            "title": "centimeters"
          }
        ]
    },
    {
        "field_map": "weightVariance",
        "item_name": "EA.weight_variance",
        "title": "Weight variance (%)",
        "hint_detail": "Potential variance in package weight expressed as a percentage.",
        "hint_panel" : "Enter the potential variance in package weight expressed as a percentage.",
        "example": "3.25%",
        "sequence": 8,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: weightVarianceValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "weightVariance",
        "item_name": "PK.weight_variance",
        "title": "Weight variance (%)",
        "hint_detail": "Potential variance in package weight expressed as a percentage.",
        "hint_panel" : "Enter the potential variance in package weight expressed as a percentage.",
        "example": "3.25%",
        "sequence": 8,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: weightVarianceValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "weightVariance",
        "item_name": "BX.weight_variance",
        "title": "Weight variance (%)",
        "hint_detail": "Potential variance in package weight expressed as a percentage.",
        "hint_panel" : "Enter the potential variance in package weight expressed as a percentage.",
        "example": "3.25%",
        "sequence": 8,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: weightVarianceValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "weightVariance",
        "item_name": "CA.weight_variance",
        "title": "Weight variance (%)",
        "hint_detail": "Potential variance in package weight expressed as a percentage.",
        "hint_panel" : "Enter the potential variance in package weight expressed as a percentage.",
        "example": "3.25%",
        "sequence": 8,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: weightVarianceValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "weightVariance",
        "item_name": "PL.weight_variance",
        "title": "Weight variance (%)",
        "hint_detail": "Potential variance in package weight expressed as a percentage.",
        "hint_panel" : "Enter the potential variance in package weight expressed as a percentage.",
        "example": "3.25%",
        "sequence": 8,
        "show_more": true,
        'numerical': true,
        'digits_limit': 8,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_8,
            message: weightVarianceValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "weight",
        "item_name": "EA.weights.weight",
        "title": "Weight",
        "hint_detail": "Gross weight per package.",
        "hint_panel" : "Enter the gross weight of this package.",
        "example": "1.08",
        "sequence": 9,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: 'Weight must be greater than 0 and less than 9 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "weight",
        "item_name": "PK.weights.weight",
        "title": "Weight",
        "hint_detail": "Gross weight per package.",
        "hint_panel" : "Enter the gross weight of this package.",
        "example": "1.08",
        "sequence": 9,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: 'Weight must be greater than 0 and less than 9 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "weight",
        "item_name": "BX.weights.weight",
        "title": "Weight",
        "hint_detail": "Gross weight per package.",
        "hint_panel" : "Enter the gross weight of this package.",
        "example": "1.08",
        "sequence": 9,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: 'Weight must be greater than 0 and less than 9 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "weight",
        "item_name": "CA.weights.weight",
        "title": "Weight",
        "hint_detail": "Gross weight per package.",
        "hint_panel" : "Enter the gross weight of this package.",
        "example": "1.08",
        "sequence": 9,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: 'Weight must be greater than 0 and less than 9 characters including 4 decimal places..'
          }
        },
        "lookup": null
    },
    {
        "field_map": "weight",
        "item_name": "PL.weights.weight",
        "title": "Weight",
        "hint_detail": "Gross weight per package.",
        "hint_panel" : "Enter the gross weight of this package.",
        "example": "1.08",
        "sequence": 9,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: 'Weight must be greater than 0 and less than 9 characters including 4 decimal places.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionalWeight",
        "item_name": "EA.weights.dimensional_weight",
        "title": "Dimensional weight",
        "hint_detail": "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package.",
        "hint_panel" : "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package. Domestic ground and air shipping formula for Dimensional Weight is (Height x Width x Length) / 194.",
        "example": "0.9444",
        "sequence": 10,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: dimensionalWeightValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionalWeight",
        "item_name": "PK.weights.dimensional_weight",
        "title": "Dimensional weight",
        "hint_detail": "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package.",
        "hint_panel" : "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package. Domestic ground and air shipping formula for Dimensional Weight is (Height x Width x Length) / 194.",
        "example": "0.9444",
        "sequence": 10,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: dimensionalWeightValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionalWeight",
        "item_name": "BX.weights.dimensional_weight",
        "title": "Dimensional weight",
        "hint_detail": "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package.",
        "hint_panel" : "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package. Domestic ground and air shipping formula for Dimensional Weight is (Height x Width x Length) / 194.",
        "example": "0.9444",
        "sequence": 10,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: dimensionalWeightValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionalWeight",
        "item_name": "CA.weights.dimensional_weight",
        "title": "Dimensional weight",
        "hint_detail": "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package.",
        "hint_panel" : "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package. Domestic ground and air shipping formula for Dimensional Weight is (Height x Width x Length) / 194.",
        "example": "0.9444",
        "sequence": 10,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: dimensionalWeightValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "dimensionalWeight",
        "item_name": "PL.weights.dimensional_weight",
        "title": "Dimensional weight",
        "hint_detail": "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package.",
        "hint_panel" : "Dimensional weight is a standard formula used throughout the air-freight industry that considers density when determining charges. Dimensional weight is determined by using the International Air Transportation Association volumetric standard. The calculations are then used to consider the amount of space your package will take up on an aircraft in relation to the actual weight of your package. Domestic ground and air shipping formula for Dimensional Weight is (Height x Width x Length) / 194.",
        "example": "0.9444",
        "sequence": 10,
        "show_more": true,
        'numerical': true,
        'digits_limit': 9,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.N4_9,
            message: dimensionalWeightValidationText,
          }
        },
        "lookup": null
    },
    {
        "field_map": "weightUom",
        "item_name": "EA.weights.uom",
        "title": "Weights unit measurement",
        "hint_detail": "Units of measurement for package weight. Gross pounds and gross kilograms are the only two options.",
        "hint_panel" : "Choose gross pounds or kilograms as the unit of measurement for package weight. These are the only two available options.",
        "example": "gross pounds",
        "sequence": 11,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Weights unit measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "PG",
            "title": "gross pounds"
          },
          {
            "option": "GT",
            "title": "gross kilograms"
          }
        ]
    },
    {
        "field_map": "weightUom",
        "item_name": "PK.weights.uom",
        "title": "Weights unit measurement",
        "hint_detail": "Units of measurement for package weight. Gross pounds and gross kilograms are the only two options.",
        "hint_panel" : "Choose gross pounds or kilograms as the unit of measurement for package weight. These are the only two available options.",
        "example": "gross pounds",
        "sequence": 11,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Weights unit measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "PG",
            "title": "gross pounds"
          },
          {
            "option": "GT",
            "title": "gross kilograms"
          }
        ]
    },
    {
        "field_map": "weightUom",
        "item_name": "BX.weights.uom",
        "title": "Weights unit measurement",
        "hint_detail": "Units of measurement for package weight. Gross pounds and gross kilograms are the only two options.",
        "hint_panel" : "Choose gross pounds or kilograms as the unit of measurement for package weight. These are the only two available options.",
        "example": "gross pounds",
        "sequence": 11,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Weights unit measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "PG",
            "title": "gross pounds"
          },
          {
            "option": "GT",
            "title": "gross kilograms"
          }
        ]
    },
    {
        "field_map": "weightUom",
        "item_name": "CA.weights.uom",
        "title": "Weights unit measurement",
        "hint_detail": "Units of measurement for package weight. Gross pounds and gross kilograms are the only two options.",
        "hint_panel" : "Choose gross pounds or kilograms as the unit of measurement for package weight. These are the only two available options.",
        "example": "gross pounds",
        "sequence": 11,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Weights unit measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "PG",
            "title": "gross pounds"
          },
          {
            "option": "GT",
            "title": "gross kilograms"
          }
        ]
    },
    {
        "field_map": "weightUom",
        "item_name": "PL.weights.uom",
        "title": "Weights unit measurement",
        "hint_detail": "Units of measurement for package weight. Gross pounds and gross kilograms are the only two options.",
        "hint_panel" : "Choose gross pounds or kilograms as the unit of measurement for package weight. These are the only two available options.",
        "example": "gross pounds",
        "sequence": 11,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            required: true,
            message: 'Weights unit measurement is required.'
          }
        },
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "PG",
            "title": "gross pounds"
          },
          {
            "option": "GT",
            "title": "gross kilograms"
          }
        ]
    },
    {
        "field_map": "packageLevelGtin",
        "item_name": "EA.package_level_GTIN",
        "title": "Package GTIN",
        "hint_detail": "Global Trade Identification Number (GTIN) in GTIN-14 format (Pack Level | Vendor | Item Num | Check Digit). ",
        "hint_panel" : "This field is coded as a Numeric (N) value, as all characters in the GTIN should be numeric. WARNING: Programs such as Microsoft Excel will drop leading zeros if the field is not formatted as text. To avoid confusion using this field, the range has been omitted and the field is now fixed at 14 characters, INCLUDING leading zeros where necessary.",
        "example": "00055544001627",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            length: 14,
            message: 'Package GTIN must be 14 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageLevelGtin",
        "item_name": "PK.package_level_GTIN",
        "title": "Package GTIN",
        "hint_detail": "Global Trade Identification Number (GTIN) in GTIN-14 format (Pack Level | Vendor | Item Num | Check Digit). ",
        "hint_panel" : "This field is coded as a Numeric (N) value, as all characters in the GTIN should be numeric. WARNING: Programs such as Microsoft Excel will drop leading zeros if the field is not formatted as text. To avoid confusion using this field, the range has been omitted and the field is now fixed at 14 characters, INCLUDING leading zeros where necessary.",
        "example": "00055544001627",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            length: 14,
            message: 'Package GTIN must be 14 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageLevelGtin",
        "item_name": "BX.package_level_GTIN",
        "title": "Package GTIN",
        "hint_detail": "Global Trade Identification Number (GTIN) in GTIN-14 format (Pack Level | Vendor | Item Num | Check Digit). ",
        "hint_panel" : "This field is coded as a Numeric (N) value, as all characters in the GTIN should be numeric. WARNING: Programs such as Microsoft Excel will drop leading zeros if the field is not formatted as text. To avoid confusion using this field, the range has been omitted and the field is now fixed at 14 characters, INCLUDING leading zeros where necessary.",
        "example": "00055544001627",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            length: 14,
            message: 'Package GTIN must be 14 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageLevelGtin",
        "item_name": "CA.package_level_GTIN",
        "title": "Package GTIN",
        "hint_detail": "Global Trade Identification Number (GTIN) in GTIN-14 format (Pack Level | Vendor | Item Num | Check Digit). ",
        "hint_panel" : "This field is coded as a Numeric (N) value, as all characters in the GTIN should be numeric. WARNING: Programs such as Microsoft Excel will drop leading zeros if the field is not formatted as text. To avoid confusion using this field, the range has been omitted and the field is now fixed at 14 characters, INCLUDING leading zeros where necessary.",
        "example": "00055544001627",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            length: 14,
            message: 'Package GTIN must be 14 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageLevelGtin",
        "item_name": "PL.package_level_GTIN",
        "title": "Package GTIN",
        "hint_detail": "Global Trade Identification Number (GTIN) in GTIN-14 format (Pack Level | Vendor | Item Num | Check Digit). ",
        "hint_panel" : "This field is coded as a Numeric (N) value, as all characters in the GTIN should be numeric. WARNING: Programs such as Microsoft Excel will drop leading zeros if the field is not formatted as text. To avoid confusion using this field, the range has been omitted and the field is now fixed at 14 characters, INCLUDING leading zeros where necessary.",
        "example": "00055544001627",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            length: 14,
            message: 'Package GTIN must be 14 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "stackingFactor",
        "item_name": "EA.stacking_factor",
        "title": "Stacking factor",
        "hint_detail": "Number of layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "hint_panel" : "Enter how many layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "example": "Eaches may be 5. Cases may be 3. Pallets may be 1.",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            minLength: 0,
            maxLength: 3,
            message: 'Stacking factor must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "stackingFactor",
        "item_name": "PK.stacking_factor",
        "title": "Stacking factor",
        "hint_detail": "Number of layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "hint_panel" : "Enter how many layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "example": "Eaches may be 5. Cases may be 3. Pallets may be 1.",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            minLength: 0,
            maxLength: 3,
            message: 'Stacking factor must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "stackingFactor",
        "item_name": "BX.stacking_factor",
        "title": "Stacking factor",
        "hint_detail": "Number of layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "hint_panel" : "Enter how many layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "example": "Eaches may be 5. Cases may be 3. Pallets may be 1.",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            minLength: 0,
            maxLength: 3,
            message: 'Stacking factor must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "stackingFactor",
        "item_name": "CA.stacking_factor",
        "title": "Stacking factor",
        "hint_detail": "Number of layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "hint_panel" : "Enter how many layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "example": "Eaches may be 5. Cases may be 3. Pallets may be 1.",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            minLength: 0,
            maxLength: 3,
            message: 'Stacking factor must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "stackingFactor",
        "item_name": "PL.stacking_factor",
        "title": "Stacking factor",
        "hint_detail": "Number of layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "hint_panel" : "Enter how many layers of package level which can be safely stacked on top of each other. Applicable to each packaging option.",
        "example": "Eaches may be 5. Cases may be 3. Pallets may be 1.",
        "sequence": 12,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            minLength: 0,
            maxLength: 3,
            message: 'Stacking factor must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageBarCodeCharacters",
        "item_name": "EA.package_bar_code_characters",
        "title": "Bar code",
        "hint_detail": "Use if package bar code characters are different from GTIN.",
        "hint_panel" : "Use if package bar code characters are different from GTIN.",
        "example": "055544001628",
        "sequence": 13,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 48,
            pattern: patterns.ANbarCode,
            message: 'Bar code must be less than 49 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageBarCodeCharacters",
        "item_name": "PK.package_bar_code_characters",
        "title": "Bar code",
        "hint_detail": "Use if package bar code characters are different from GTIN.",
        "hint_panel" : "Use if package bar code characters are different from GTIN.",
        "example": "055544001628",
        "sequence": 13,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 48,
            pattern: patterns.ANbarCode,
            message: 'Bar code must be less than 49 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageBarCodeCharacters",
        "item_name": "BX.package_bar_code_characters",
        "title": "Bar code",
        "hint_detail": "Use if package bar code characters are different from GTIN.",
        "hint_panel" : "Use if package bar code characters are different from GTIN.",
        "example": "055544001628",
        "sequence": 13,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 48,
            pattern: patterns.ANbarCode,
            message: 'Bar code must be less than 49 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageBarCodeCharacters",
        "item_name": "CA.package_bar_code_characters",
        "title": "Bar code",
        "hint_detail": "Use if package bar code characters are different from GTIN.",
        "hint_panel" : "Use if package bar code characters are different from GTIN.",
        "example": "055544001628",
        "sequence": 13,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 48,
            pattern: patterns.ANbarCode,
            message: 'Bar code must be less than 49 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "packageBarCodeCharacters",
        "item_name": "PL.package_bar_code_characters",
        "title": "Bar code",
        "hint_detail": "Use if package bar code characters are different from GTIN.",
        "hint_panel" : "Use if package bar code characters are different from GTIN.",
        "example": "055544001628",
        "sequence": 13,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 48,
            pattern: patterns.ANbarCode,
            message: 'Bar code must be less than 49 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "electronicProductCode",
        "item_name": "EA.electronic_product_code",
        "title": "Electronic code",
        "hint_detail": "Written form of RFID code (electronic barcode).",
        "hint_panel" : "Written form of RFID code (electronic barcode). For reference visit www.epcglobalinc.org/standards",
        "example": "055544001628",
        "sequence": 14,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 27,
            message: 'Electronic code must be exactly 27 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "electronicProductCode",
        "item_name": "PK.electronic_product_code",
        "title": "Electronic code",
        "hint_detail": "Written form of RFID code (electronic barcode).",
        "hint_panel" : "Written form of RFID code (electronic barcode). For reference visit www.epcglobalinc.org/standards",
        "example": "055544001628",
        "sequence": 14,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 27,
            message: 'Electronic code must be exactly 27 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "electronicProductCode",
        "item_name": "BX.electronic_product_code",
        "title": "Electronic code",
        "hint_detail": "Written form of RFID code (electronic barcode).",
        "hint_panel" : "Written form of RFID code (electronic barcode). For reference visit www.epcglobalinc.org/standards",
        "example": "055544001628",
        "sequence": 14,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 27,
            message: 'Electronic code must be exactly 27 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "electronicProductCode",
        "item_name": "CA.electronic_product_code",
        "title": "Electronic code",
        "hint_detail": "Written form of RFID code (electronic barcode).",
        "hint_panel" : "Written form of RFID code (electronic barcode). For reference visit www.epcglobalinc.org/standards",
        "example": "055544001628",
        "sequence": 14,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 27,
            message: 'Electronic code must be exactly 27 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "electronicProductCode",
        "item_name": "PL.electronic_product_code",
        "title": "Electronic code",
        "hint_detail": "Written form of RFID code (electronic barcode).",
        "hint_panel" : "Written form of RFID code (electronic barcode). For reference visit www.epcglobalinc.org/standards",
        "example": "055544001628",
        "sequence": 14,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 27,
            message: 'Electronic code must be exactly 27 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "innerQuantity",
        "item_name": "EA.inner_quantity",
        "title": "Inner quantity",
        "hint_detail": "Inner quantity of items within this package.",
        "hint_panel" : "Enter the inner quantity of items in this package type.",
        "example": "1",
        "sequence": 15,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.R1_8,
            message: 'Inner quantity must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "innerQuantity",
        "item_name": "PK.inner_quantity",
        "title": "Inner quantity",
        "hint_detail": "Inner quantity of items within this package.",
        "hint_panel" : "Enter the inner quantity of items in this package type.",
        "example": "2",
        "sequence": 15,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.R1_8,
            message: 'Inner quantity must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "innerQuantity",
        "item_name": "BX.inner_quantity",
        "title": "Inner quantity",
        "hint_detail": "Inner quantity of items within this package.",
        "hint_panel" : "Enter the inner quantity of items in this package type.",
        "example": "5",
        "sequence": 15,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.R1_8,
            message: 'Inner quantity must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "innerQuantity",
        "item_name": "CA.inner_quantity",
        "title": "Inner quantity",
        "hint_detail": "Inner quantity of items within this package.",
        "hint_panel" : "Enter the inner quantity of items in this package type.",
        "example": "30",
        "sequence": 15,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.R1_8,
            message: 'Inner quantity must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "innerQuantity",
        "item_name": "PL.inner_quantity",
        "title": "Inner quantity",
        "hint_detail": "Inner quantity of items within this package.",
        "hint_panel" : "Enter the inner quantity of items in this package type.",
        "example": "120",
        "sequence": 15,
        "show_more": true,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            min: 0,
            pattern: patterns.R1_8,
            message: 'Inner quantity must be greater than 0.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "innerQuantityUom",
        "item_name": "EA.inner_quantity_uom",
        "title": "",
        "hint_detail": "Unit of measure for the inner quantity.",
        "hint_panel" : "Enter the unit of measure for the inner quantity.",
        "example": "Each",
        "sequence": 16,
        "show_more": true,
        "lookup": [
          { 'option': '', 'title': '-' },
          { 'option': 'OZ', 'title': 'Ounces' },
          { 'option': 'AS', 'title': 'Assortment' },
          { 'option': 'BG', 'title': 'Bag' },
          { 'option': 'BK', 'title': 'Book' },
          { 'option': 'BO', 'title': 'Bottle' },
          { 'option': 'BX', 'title': 'Box' },
          { 'option': 'BC', 'title': 'Bucket' },
          { 'option': 'BD', 'title': 'Bundle' },
          { 'option': 'CN', 'title': 'Can' },
          { 'option': 'CG', 'title': 'Card' },
          { 'option': 'CT', 'title': 'Carton' },
          { 'option': 'CQ', 'title': 'Cartridge' },
          { 'option': 'CA', 'title': 'Case' },
          { 'option': 'CX', 'title': 'Coil' },
          { 'option': 'CH', 'title': 'Container' },
          { 'option': 'FN', 'title': 'Clamshell' },
          { 'option': 'CP', 'title': 'Crate' },
          { 'option': 'DZ', 'title': 'Dozen' },
          { 'option': 'DR', 'title': 'Drum' },
          { 'option': 'EA', 'title': 'Each' },
          { 'option': 'EV', 'title': 'Envelope' },
          { 'option': 'FT', 'title': 'Foot' },
          { 'option': 'GA', 'title': 'Gallon' },
          { 'option': 'HU', 'title': 'Hundred' },
          { 'option': 'HC', 'title': 'Hundred count' },
          { 'option': 'JR', 'title': 'Jar' },
          { 'option': 'KE', 'title': 'Keg' },
          { 'option': 'KG', 'title': 'Kilogram' },
          { 'option': 'KT', 'title': 'Kit' },
          { 'option': 'LT', 'title': 'Liter' },
          { 'option': 'MR', 'title': 'Meter' },
          { 'option': 'PH', 'title': 'Pack (PAK)' },
          { 'option': 'PK', 'title': 'Package' },
          { 'option': 'PA', 'title': 'Pail' },
          { 'option': 'PR', 'title': 'Pair' },
          { 'option': 'PF', 'title': 'Pallet' },
          { 'option': 'PL', 'title': 'Pallet unit load' },
          { 'option': 'PC', 'title': 'Piece' },
          { 'option': 'PT', 'title': 'Pint' },
          { 'option': 'LB', 'title': 'Pound' },
          { 'option': 'QT', 'title': 'Quart' },
          { 'option': 'RE', 'title': 'Reel' },
          { 'option': 'RD', 'title': 'Rod' },
          { 'option': 'RL', 'title': 'Roll' },
          { 'option': 'ST', 'title': 'Set' },
          { 'option': 'SH', 'title': 'Sheet' },
          { 'option': 'SL', 'title': 'Sleeve' },
          { 'option': 'SO', 'title': 'Spool' },
          { 'option': '15', 'title': 'Stick' },
          { 'option': 'TH', 'title': 'Thousand' },
          { 'option': 'TY', 'title': 'Tray' },
          { 'option': 'TB', 'title': 'Tube' },
          { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
        "field_map": "innerQuantityUom",
        "item_name": "PK.inner_quantity_uom",
        "title": "",
        "hint_detail": "Unit of measure for the inner quantity.",
        "hint_panel" : "Enter the unit of measure for the inner quantity.",
        "example": "Each",
        "sequence": 16,
        "show_more": true,
        "lookup": [
          { 'option': '', 'title': '-' },
          { 'option': 'OZ', 'title': 'Ounces' },
          { 'option': 'AS', 'title': 'Assortment' },
          { 'option': 'BG', 'title': 'Bag' },
          { 'option': 'BK', 'title': 'Book' },
          { 'option': 'BO', 'title': 'Bottle' },
          { 'option': 'BX', 'title': 'Box' },
          { 'option': 'BC', 'title': 'Bucket' },
          { 'option': 'BD', 'title': 'Bundle' },
          { 'option': 'CN', 'title': 'Can' },
          { 'option': 'CG', 'title': 'Card' },
          { 'option': 'CT', 'title': 'Carton' },
          { 'option': 'CQ', 'title': 'Cartridge' },
          { 'option': 'CA', 'title': 'Case' },
          { 'option': 'CX', 'title': 'Coil' },
          { 'option': 'CH', 'title': 'Container' },
          { 'option': 'FN', 'title': 'Clamshell' },
          { 'option': 'CP', 'title': 'Crate' },
          { 'option': 'DZ', 'title': 'Dozen' },
          { 'option': 'DR', 'title': 'Drum' },
          { 'option': 'EA', 'title': 'Each' },
          { 'option': 'EV', 'title': 'Envelope' },
          { 'option': 'FT', 'title': 'Foot' },
          { 'option': 'GA', 'title': 'Gallon' },
          { 'option': 'HU', 'title': 'Hundred' },
          { 'option': 'HC', 'title': 'Hundred count' },
          { 'option': 'JR', 'title': 'Jar' },
          { 'option': 'KE', 'title': 'Keg' },
          { 'option': 'KG', 'title': 'Kilogram' },
          { 'option': 'KT', 'title': 'Kit' },
          { 'option': 'LT', 'title': 'Liter' },
          { 'option': 'MR', 'title': 'Meter' },
          { 'option': 'PH', 'title': 'Pack (PAK)' },
          { 'option': 'PK', 'title': 'Package' },
          { 'option': 'PA', 'title': 'Pail' },
          { 'option': 'PR', 'title': 'Pair' },
          { 'option': 'PF', 'title': 'Pallet' },
          { 'option': 'PL', 'title': 'Pallet unit load' },
          { 'option': 'PC', 'title': 'Piece' },
          { 'option': 'PT', 'title': 'Pint' },
          { 'option': 'LB', 'title': 'Pound' },
          { 'option': 'QT', 'title': 'Quart' },
          { 'option': 'RE', 'title': 'Reel' },
          { 'option': 'RD', 'title': 'Rod' },
          { 'option': 'RL', 'title': 'Roll' },
          { 'option': 'ST', 'title': 'Set' },
          { 'option': 'SH', 'title': 'Sheet' },
          { 'option': 'SL', 'title': 'Sleeve' },
          { 'option': 'SO', 'title': 'Spool' },
          { 'option': '15', 'title': 'Stick' },
          { 'option': 'TH', 'title': 'Thousand' },
          { 'option': 'TY', 'title': 'Tray' },
          { 'option': 'TB', 'title': 'Tube' },
          { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
        "field_map": "innerQuantityUom",
        "item_name": "BX.inner_quantity_uom",
        "title": "",
        "hint_detail": "Unit of measure for the inner quantity.",
        "hint_panel" : "Enter the unit of measure for the inner quantity.",
        "example": "Each",
        "sequence": 16,
        "show_more": true,
        "lookup": [
          { 'option': '', 'title': '-' },
          { 'option': 'OZ', 'title': 'Ounces' },
          { 'option': 'AS', 'title': 'Assortment' },
          { 'option': 'BG', 'title': 'Bag' },
          { 'option': 'BK', 'title': 'Book' },
          { 'option': 'BO', 'title': 'Bottle' },
          { 'option': 'BX', 'title': 'Box' },
          { 'option': 'BC', 'title': 'Bucket' },
          { 'option': 'BD', 'title': 'Bundle' },
          { 'option': 'CN', 'title': 'Can' },
          { 'option': 'CG', 'title': 'Card' },
          { 'option': 'CT', 'title': 'Carton' },
          { 'option': 'CQ', 'title': 'Cartridge' },
          { 'option': 'CA', 'title': 'Case' },
          { 'option': 'CX', 'title': 'Coil' },
          { 'option': 'CH', 'title': 'Container' },
          { 'option': 'FN', 'title': 'Clamshell' },
          { 'option': 'CP', 'title': 'Crate' },
          { 'option': 'DZ', 'title': 'Dozen' },
          { 'option': 'DR', 'title': 'Drum' },
          { 'option': 'EA', 'title': 'Each' },
          { 'option': 'EV', 'title': 'Envelope' },
          { 'option': 'FT', 'title': 'Foot' },
          { 'option': 'GA', 'title': 'Gallon' },
          { 'option': 'HU', 'title': 'Hundred' },
          { 'option': 'HC', 'title': 'Hundred count' },
          { 'option': 'JR', 'title': 'Jar' },
          { 'option': 'KE', 'title': 'Keg' },
          { 'option': 'KG', 'title': 'Kilogram' },
          { 'option': 'KT', 'title': 'Kit' },
          { 'option': 'LT', 'title': 'Liter' },
          { 'option': 'MR', 'title': 'Meter' },
          { 'option': 'PH', 'title': 'Pack (PAK)' },
          { 'option': 'PK', 'title': 'Package' },
          { 'option': 'PA', 'title': 'Pail' },
          { 'option': 'PR', 'title': 'Pair' },
          { 'option': 'PF', 'title': 'Pallet' },
          { 'option': 'PL', 'title': 'Pallet unit load' },
          { 'option': 'PC', 'title': 'Piece' },
          { 'option': 'PT', 'title': 'Pint' },
          { 'option': 'LB', 'title': 'Pound' },
          { 'option': 'QT', 'title': 'Quart' },
          { 'option': 'RE', 'title': 'Reel' },
          { 'option': 'RD', 'title': 'Rod' },
          { 'option': 'RL', 'title': 'Roll' },
          { 'option': 'ST', 'title': 'Set' },
          { 'option': 'SH', 'title': 'Sheet' },
          { 'option': 'SL', 'title': 'Sleeve' },
          { 'option': 'SO', 'title': 'Spool' },
          { 'option': '15', 'title': 'Stick' },
          { 'option': 'TH', 'title': 'Thousand' },
          { 'option': 'TY', 'title': 'Tray' },
          { 'option': 'TB', 'title': 'Tube' },
          { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
        "field_map": "innerQuantityUom",
        "item_name": "CA.inner_quantity_uom",
        "title": "",
        "hint_detail": "Unit of measure for the inner quantity.",
        "hint_panel" : "Enter the unit of measure for the inner quantity.",
        "example": "Each",
        "sequence": 16,
        "show_more": true,
        "lookup": [
          { 'option': '', 'title': '-' },
          { 'option': 'OZ', 'title': 'Ounces' },
          { 'option': 'AS', 'title': 'Assortment' },
          { 'option': 'BG', 'title': 'Bag' },
          { 'option': 'BK', 'title': 'Book' },
          { 'option': 'BO', 'title': 'Bottle' },
          { 'option': 'BX', 'title': 'Box' },
          { 'option': 'BC', 'title': 'Bucket' },
          { 'option': 'BD', 'title': 'Bundle' },
          { 'option': 'CN', 'title': 'Can' },
          { 'option': 'CG', 'title': 'Card' },
          { 'option': 'CT', 'title': 'Carton' },
          { 'option': 'CQ', 'title': 'Cartridge' },
          { 'option': 'CA', 'title': 'Case' },
          { 'option': 'CX', 'title': 'Coil' },
          { 'option': 'CH', 'title': 'Container' },
          { 'option': 'FN', 'title': 'Clamshell' },
          { 'option': 'CP', 'title': 'Crate' },
          { 'option': 'DZ', 'title': 'Dozen' },
          { 'option': 'DR', 'title': 'Drum' },
          { 'option': 'EA', 'title': 'Each' },
          { 'option': 'EV', 'title': 'Envelope' },
          { 'option': 'FT', 'title': 'Foot' },
          { 'option': 'GA', 'title': 'Gallon' },
          { 'option': 'HU', 'title': 'Hundred' },
          { 'option': 'HC', 'title': 'Hundred count' },
          { 'option': 'JR', 'title': 'Jar' },
          { 'option': 'KE', 'title': 'Keg' },
          { 'option': 'KG', 'title': 'Kilogram' },
          { 'option': 'KT', 'title': 'Kit' },
          { 'option': 'LT', 'title': 'Liter' },
          { 'option': 'MR', 'title': 'Meter' },
          { 'option': 'PH', 'title': 'Pack (PAK)' },
          { 'option': 'PK', 'title': 'Package' },
          { 'option': 'PA', 'title': 'Pail' },
          { 'option': 'PR', 'title': 'Pair' },
          { 'option': 'PF', 'title': 'Pallet' },
          { 'option': 'PL', 'title': 'Pallet unit load' },
          { 'option': 'PC', 'title': 'Piece' },
          { 'option': 'PT', 'title': 'Pint' },
          { 'option': 'LB', 'title': 'Pound' },
          { 'option': 'QT', 'title': 'Quart' },
          { 'option': 'RE', 'title': 'Reel' },
          { 'option': 'RD', 'title': 'Rod' },
          { 'option': 'RL', 'title': 'Roll' },
          { 'option': 'ST', 'title': 'Set' },
          { 'option': 'SH', 'title': 'Sheet' },
          { 'option': 'SL', 'title': 'Sleeve' },
          { 'option': 'SO', 'title': 'Spool' },
          { 'option': '15', 'title': 'Stick' },
          { 'option': 'TH', 'title': 'Thousand' },
          { 'option': 'TY', 'title': 'Tray' },
          { 'option': 'TB', 'title': 'Tube' },
          { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
        "field_map": "innerQuantityUom",
        "item_name": "PL.inner_quantity_uom",
        "title": "",
        "hint_detail": "Unit of measure for the inner quantity.",
        "hint_panel" : "Enter the unit of measure for the inner quantity.",
        "example": "Each",
        "sequence": 16,
        "show_more": true,
        "lookup": [
          { 'option': '', 'title': '-' },
          { 'option': 'OZ', 'title': 'Ounces' },
          { 'option': 'AS', 'title': 'Assortment' },
          { 'option': 'BG', 'title': 'Bag' },
          { 'option': 'BK', 'title': 'Book' },
          { 'option': 'BO', 'title': 'Bottle' },
          { 'option': 'BX', 'title': 'Box' },
          { 'option': 'BC', 'title': 'Bucket' },
          { 'option': 'BD', 'title': 'Bundle' },
          { 'option': 'CN', 'title': 'Can' },
          { 'option': 'CG', 'title': 'Card' },
          { 'option': 'CT', 'title': 'Carton' },
          { 'option': 'CQ', 'title': 'Cartridge' },
          { 'option': 'CA', 'title': 'Case' },
          { 'option': 'CX', 'title': 'Coil' },
          { 'option': 'CH', 'title': 'Container' },
          { 'option': 'FN', 'title': 'Clamshell' },
          { 'option': 'CP', 'title': 'Crate' },
          { 'option': 'DZ', 'title': 'Dozen' },
          { 'option': 'DR', 'title': 'Drum' },
          { 'option': 'EA', 'title': 'Each' },
          { 'option': 'EV', 'title': 'Envelope' },
          { 'option': 'FT', 'title': 'Foot' },
          { 'option': 'GA', 'title': 'Gallon' },
          { 'option': 'HU', 'title': 'Hundred' },
          { 'option': 'HC', 'title': 'Hundred count' },
          { 'option': 'JR', 'title': 'Jar' },
          { 'option': 'KE', 'title': 'Keg' },
          { 'option': 'KG', 'title': 'Kilogram' },
          { 'option': 'KT', 'title': 'Kit' },
          { 'option': 'LT', 'title': 'Liter' },
          { 'option': 'MR', 'title': 'Meter' },
          { 'option': 'PH', 'title': 'Pack (PAK)' },
          { 'option': 'PK', 'title': 'Package' },
          { 'option': 'PA', 'title': 'Pail' },
          { 'option': 'PR', 'title': 'Pair' },
          { 'option': 'PF', 'title': 'Pallet' },
          { 'option': 'PL', 'title': 'Pallet unit load' },
          { 'option': 'PC', 'title': 'Piece' },
          { 'option': 'PT', 'title': 'Pint' },
          { 'option': 'LB', 'title': 'Pound' },
          { 'option': 'QT', 'title': 'Quart' },
          { 'option': 'RE', 'title': 'Reel' },
          { 'option': 'RD', 'title': 'Rod' },
          { 'option': 'RL', 'title': 'Roll' },
          { 'option': 'ST', 'title': 'Set' },
          { 'option': 'SH', 'title': 'Sheet' },
          { 'option': 'SL', 'title': 'Sleeve' },
          { 'option': 'SO', 'title': 'Spool' },
          { 'option': '15', 'title': 'Stick' },
          { 'option': 'TH', 'title': 'Thousand' },
          { 'option': 'TY', 'title': 'Tray' },
          { 'option': 'TB', 'title': 'Tube' },
          { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
      "field_map": "languageCode",
      "item_name": "EA.hazardous_materials.0.language",
      'title': 'Language',
      'hint_detail': 'Language in which this description is written.',
      'hint_panel' : 'Choose the language which this description is written.',
      'example': 'English',
      'sequence': 5,
      'details_hide': true,
      'show_more': true,
      'lookup': [
        {
          "option": "EN",
          "title": "English"
        },
        {
          "option": "ES",
          "title": "Spanish"
        },
        {
          "option": "FR",
          "title": "French"
        }
      ]
    },
    {
      "field_map": "languageCode",
      "item_name": "PK.hazardous_materials.0.language",
      'title': 'Language',
      'hint_detail': 'Language in which this description is written.',
      'hint_panel' : 'Choose the language which this description is written.',
      'example': 'English',
      'sequence': 5,
      'details_hide': true,
      'show_more': true,
      'lookup': [
        {
          "option": "EN",
          "title": "English"
        },
        {
          "option": "ES",
          "title": "Spanish"
        },
        {
          "option": "FR",
          "title": "French"
        }
      ]
    },
    {
      "field_map": "languageCode",
      "item_name": "BX.hazardous_materials.0.language",
      'title': 'Language',
      'hint_detail': 'Language in which this description is written.',
      'hint_panel' : 'Choose the language which this description is written.',
      'example': 'English',
      'sequence': 5,
      'details_hide': true,
      'show_more': true,
      'lookup': [
        {
          "option": "EN",
          "title": "English"
        },
        {
          "option": "ES",
          "title": "Spanish"
        },
        {
          "option": "FR",
          "title": "French"
        }
      ]
    },
    {
      "field_map": "languageCode",
      "item_name": "CA.hazardous_materials.0.language",
      'title': 'Language',
      'hint_detail': 'Language in which this description is written.',
      'hint_panel' : 'Choose the language which this description is written.',
      'example': 'English',
      'sequence': 5,
      'details_hide': true,
      'show_more': true,
      'lookup': [
        {
          "option": "EN",
          "title": "English"
        },
        {
          "option": "ES",
          "title": "Spanish"
        },
        {
          "option": "FR",
          "title": "French"
        }
      ]
    },
    {
      "field_map": "languageCode",
      "item_name": "PL.hazardous_materials.0.language",
      'title': 'Language',
      'hint_detail': 'Language in which this description is written.',
      'hint_panel' : 'Choose the language which this description is written.',
      'example': 'English',
      'sequence': 5,
      'details_hide': true,
      'show_more': true,
      'lookup': [
        {
          "option": "EN",
          "title": "English"
        },
        {
          "option": "ES",
          "title": "Spanish"
        },
        {
          "option": "FR",
          "title": "French"
        }
      ]
    },
    {
        "field_map": "shippingScope",
        "item_name": "EA.hazardous_materials.0.shipping_scope",
        "title": "Shipping scope",
        "hint_detail": "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "hint_panel" : "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "example": "Domestic",
        "sequence": 17,
        "show_more": true,
        "lookup": [
          {
            'option': 'DOM',
            'title': 'Domestic'
          },
          {
            'option': 'INT',
            'title': 'International'
          },
        ]
    },
    {
        "field_map": "shippingScope",
        "item_name": "PK.hazardous_materials.0.shipping_scope",
        "title": "Shipping scope",
        "hint_detail": "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "hint_panel" : "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "example": "Domestic",
        "sequence": 17,
        "show_more": true,
        "lookup": [
          {
            'option': 'DOM',
            'title': 'Domestic'
          },
          {
            'option': 'INT',
            'title': 'International'
          },
        ]
    },
    {
        "field_map": "shippingScope",
        "item_name": "BX.hazardous_materials.0.shipping_scope",
        "title": "Shipping scope",
        "hint_detail": "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "hint_panel" : "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "example": "Domestic",
        "sequence": 17,
        "show_more": true,
        "lookup": [
          {
            'option': 'DOM',
            'title': 'Domestic'
          },
          {
            'option': 'INT',
            'title': 'International'
          },
        ]
    },
    {
        "field_map": "shippingScope",
        "item_name": "CA.hazardous_materials.0.shipping_scope",
        "title": "Shipping scope",
        "hint_detail": "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "hint_panel" : "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "example": "Domestic",
        "sequence": 17,
        "show_more": true,
        "lookup": [
          {
            'option': 'DOM',
            'title': 'Domestic'
          },
          {
            'option': 'INT',
            'title': 'International'
          },
        ]
    },
    {
        "field_map": "shippingScope",
        "item_name": "PL.hazardous_materials.0.shipping_scope",
        "title": "Shipping scope",
        "hint_detail": "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "hint_panel" : "Indicates whether you (the source location) are shipping Domestically (DOM) or internationally (INT) <br>DOM = Domestic <br> INT = International",
        "example": "Domestic",
        "sequence": 17,
        "show_more": true,
        "lookup": [
          {
            'option': 'DOM',
            'title': 'Domestic'
          },
          {
            'option': 'INT',
            'title': 'International'
          },
        ]
    },
    {
        "field_map": "regulatingCountry",
        "item_name": "EA.hazardous_materials.0.regulating_country",
        "title": "Regulating country",
        "hint_detail": "Using the ISO country codes, the country from which the goods are initially shipped.",
        "hint_panel" : "Identify, using the ISO country codes, the country from which the goods are initially shipped. <br>NOTE: This is different from the documentation you may require if shipping across several countries (IE â€“ Country of Origin â€“ US; Trans-ship to France via UK)",
        "example": "United States",
        "sequence": 19,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "regulatingCountry",
        "item_name": "PK.hazardous_materials.0.regulating_country",
        "title": "Regulating country",
        "hint_detail": "Using the ISO country codes, the country from which the goods are initially shipped.",
        "hint_panel" : "Identify, using the ISO country codes, the country from which the goods are initially shipped. <br>NOTE: This is different from the documentation you may require if shipping across several countries (IE â€“ Country of Origin â€“ US; Trans-ship to France via UK)",
        "example": "United States",
        "sequence": 19,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "regulatingCountry",
        "item_name": "BX.hazardous_materials.0.regulating_country",
        "title": "Regulating country",
        "hint_detail": "Using the ISO country codes, the country from which the goods are initially shipped.",
        "hint_panel" : "Identify, using the ISO country codes, the country from which the goods are initially shipped. <br>NOTE: This is different from the documentation you may require if shipping across several countries (IE â€“ Country of Origin â€“ US; Trans-ship to France via UK)",
        "example": "United States",
        "sequence": 19,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "regulatingCountry",
        "item_name": "CA.hazardous_materials.0.regulating_country",
        "title": "Regulating country",
        "hint_detail": "Using the ISO country codes, the country from which the goods are initially shipped.",
        "hint_panel" : "Identify, using the ISO country codes, the country from which the goods are initially shipped. <br>NOTE: This is different from the documentation you may require if shipping across several countries (IE â€“ Country of Origin â€“ US; Trans-ship to France via UK)",
        "example": "United States",
        "sequence": 19,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "regulatingCountry",
        "item_name": "PL.hazardous_materials.0.regulating_country",
        "title": "Regulating country",
        "hint_detail": "Using the ISO country codes, the country from which the goods are initially shipped.",
        "hint_panel" : "Identify, using the ISO country codes, the country from which the goods are initially shipped. <br>NOTE: This is different from the documentation you may require if shipping across several countries (IE â€“ Country of Origin â€“ US; Trans-ship to France via UK)",
        "example": "United States",
        "sequence": 19,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "bulk",
        "item_name": "EA.hazardous_materials.0.bulk",
        "title": "Bulk",
        "hint_detail": "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "hint_panel" : "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "example": "Bulk",
        "sequence": 18,
        "show_more": true,
        "lookup": [
          {
            "option": "N",
            "title": "Non bulk"
          },
          {
            "option": "B",
            "title": "Bulk"
          }
        ]
    },
    {
        "field_map": "bulk",
        "item_name": "PK.hazardous_materials.0.bulk",
        "title": "Bulk",
        "hint_detail": "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "hint_panel" : "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "example": "Bulk",
        "sequence": 18,
        "show_more": true,
        "lookup": [
          {
            "option": "N",
            "title": "Non bulk"
          },
          {
            "option": "B",
            "title": "Bulk"
          }
        ]
    },
    {
        "field_map": "bulk",
        "item_name": "BX.hazardous_materials.0.bulk",
        "title": "Bulk",
        "hint_detail": "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "hint_panel" : "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "example": "Bulk",
        "sequence": 18,
        "show_more": true,
        "lookup": [
          {
            "option": "N",
            "title": "Non bulk"
          },
          {
            "option": "B",
            "title": "Bulk"
          }
        ]
    },
    {
        "field_map": "bulk",
        "item_name": "CA.hazardous_materials.0.bulk",
        "title": "Bulk",
        "hint_detail": "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "hint_panel" : "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "example": "Bulk",
        "sequence": 18,
        "show_more": true,
        "lookup": [
          {
            "option": "N",
            "title": "Non bulk"
          },
          {
            "option": "B",
            "title": "Bulk"
          }
        ]
    },
    {
        "field_map": "bulk",
        "item_name": "PL.hazardous_materials.0.bulk",
        "title": "Bulk",
        "hint_detail": "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "hint_panel" : "Indicates whether the goods shipped are being shipped in bulk (B) or non-bulk (N). <br>B = Bulk <br>N = Non-Bulk",
        "example": "Bulk",
        "sequence": 18,
        "show_more": true,
        "lookup": [
          {
            "option": "N",
            "title": "Non bulk"
          },
          {
            "option": "B",
            "title": "Bulk"
          }
        ]
    },
    {
        "field_map": "transportMethod",
        "item_name": "EA.hazardous_materials.0.transport_method",
        "title": "Transport method",
        "hint_detail": "Indicates the principal mode of shipping. ",
        "hint_panel" : "Indicates the long-haul method of shipping used: Air, Rail, Truck, Vessel. NOTE: if country of origin is Canada, replace Truck with Road, and Vessel with Marine.",
        "example": "Air",
        "sequence": 20,
        "show_more": true,
        "lookup": [
          {
            "option": "",
            "title": "-"
          },
          {
            "option": "A",
            "title": "Air"
          },
          {
            "option": "R",
            "title": "Rail"
          },
          {
            "option": "T",
            "title": "Truck (Canada: \"Road\")"
          },
          {
            "option": "V",
            "title": "Vessel (Canada: \"Marine\")"
          }
        ]
    },
    {
        "field_map": "transportMethod",
        "item_name": "PK.hazardous_materials.0.transport_method",
        "title": "Transport method",
        "hint_detail": "Indicates the principal mode of shipping. ",
        "hint_panel" : "Indicates the long-haul method of shipping used: Air, Rail, Truck, Vessel. NOTE: if country of origin is Canada, replace Truck with Road, and Vessel with Marine.",
        "example": "Air",
        "sequence": 20,
        "show_more": true,
        "lookup": [
          {
            "option": "A",
            "title": "Air"
          },
          {
            "option": "R",
            "title": "Rail"
          },
          {
            "option": "T",
            "title": "Truck (Canada: \"Road\")"
          },
          {
            "option": "V",
            "title": "Vessel (Canada: \"Marine\")"
          }
        ]
    },
    {
        "field_map": "transportMethod",
        "item_name": "BX.hazardous_materials.0.transport_method",
        "title": "Transport method",
        "hint_detail": "Indicates the principal mode of shipping. ",
        "hint_panel" : "Indicates the long-haul method of shipping used: Air, Rail, Truck, Vessel. NOTE: if country of origin is Canada, replace Truck with Road, and Vessel with Marine.",
        "example": "Air",
        "sequence": 20,
        "show_more": true,
        "lookup": [
          {
            "option": "A",
            "title": "Air"
          },
          {
            "option": "R",
            "title": "Rail"
          },
          {
            "option": "T",
            "title": "Truck (Canada: \"Road\")"
          },
          {
            "option": "V",
            "title": "Vessel (Canada: \"Marine\")"
          }
        ]
    },
    {
        "field_map": "transportMethod",
        "item_name": "CA.hazardous_materials.0.transport_method",
        "title": "Transport method",
        "hint_detail": "Indicates the principal mode of shipping. ",
        "hint_panel" : "Indicates the long-haul method of shipping used: Air, Rail, Truck, Vessel. NOTE: if country of origin is Canada, replace Truck with Road, and Vessel with Marine.",
        "example": "Air",
        "sequence": 20,
        "show_more": true,
        "lookup": [
          {
            "option": "A",
            "title": "Air"
          },
          {
            "option": "R",
            "title": "Rail"
          },
          {
            "option": "T",
            "title": "Truck (Canada: \"Road\")"
          },
          {
            "option": "V",
            "title": "Vessel (Canada: \"Marine\")"
          }
        ]
    },
    {
        "field_map": "transportMethod",
        "item_name": "PL.hazardous_materials.0.transport_method",
        "title": "Transport method",
        "hint_detail": "Indicates the principal mode of shipping. ",
        "hint_panel" : "Indicates the long-haul method of shipping used: Air, Rail, Truck, Vessel. NOTE: if country of origin is Canada, replace Truck with Road, and Vessel with Marine.",
        "example": "Air",
        "sequence": 20,
        "show_more": true,
        "lookup": [
          {
            "option": "A",
            "title": "Air"
          },
          {
            "option": "R",
            "title": "Rail"
          },
          {
            "option": "T",
            "title": "Truck (Canada: \"Road\")"
          },
          {
            "option": "V",
            "title": "Vessel (Canada: \"Marine\")"
          }
        ]
    },
    {
        "field_map": "regulated",
        "item_name": "EA.hazardous_materials.0.regulated.regulated",
        "title": "Regulated",
        "hint_detail": "Indicates if the item is regulated.",
        "hint_panel" : "Yes or No if the goods are regulated or not.",
        "example": "Yes",
        "sequence": 21,
        "show_more": true,
        "lookup": yesNoLookup,
    },
    {
        "field_map": "regulated",
        "item_name": "PK.hazardous_materials.0.regulated.regulated",
        "title": "Regulated",
        "hint_detail": "Indicates if the item is regulated.",
        "hint_panel" : "Yes or No if the goods are regulated or not.",
        "example": "Yes",
        "sequence": 21,
        "show_more": true,
        "lookup": yesNoLookup,
    },
    {
        "field_map": "regulated",
        "item_name": "BX.hazardous_materials.0.regulated.regulated",
        "title": "Regulated",
        "hint_detail": "Indicates if the item is regulated.",
        "hint_panel" : "Yes or No if the goods are regulated or not.",
        "example": "Yes",
        "sequence": 21,
        "show_more": true,
        "lookup": yesNoLookup,
    },
    {
        "field_map": "regulated",
        "item_name": "CA.hazardous_materials.0.regulated.regulated",
        "hint_detail": "Indicates if the item is regulated.",
        "hint_panel" : "Yes or No if the goods are regulated or not.",
        "example": "Yes",
        "sequence": 21,
        "show_more": true,
        "lookup": yesNoLookup,
    },
    {
        "field_map": "regulated",
        "item_name": "PL.hazardous_materials.0.regulated.regulated",
        "hint_detail": "Indicates if the item is regulated.",
        "hint_panel" : "Yes or No if the goods are regulated or not.",
        "example": "Yes",
        "sequence": 21,
        "show_more": true,
        "lookup": yesNoLookup,
    },
    {
        "field_map": "hazardousClass",
        "item_name": "EA.hazardous_materials.0.regulated.hazardous_class",
        "title": "Hazardous class (separate with commas)",
        "hint_detail": "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "hint_panel" : "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "example": "2.3,3.2,ORM-AIR",
        "sequence": 21,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength:10,
            required: false,
            message: 'Hazardous class is required when regulated and must be no more than 10 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousClass",
        "item_name": "PK.hazardous_materials.0.regulated.hazardous_class",
        "title": "Hazardous class (separate with commas)",
        "hint_detail": "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "hint_panel" : "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "example": "2.3,3.2,ORM-AIR",
        "sequence": 21,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength:10,
            required: false,
            message: 'Hazardous class is required when regulated and must be no more than 10 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousClass",
        "item_name": "BX.hazardous_materials.0.regulated.hazardous_class",
        "title": "Hazardous class (separate with commas)",
        "hint_detail": "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "hint_panel" : "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "example": "2.3,3.2,ORM-AIR",
        "sequence": 21,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength:10,
            required: false,
            message: 'Hazardous class is required when regulated and must be no more than 10 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousClass",
        "item_name": "CA.hazardous_materials.0.regulated.hazardous_class",
        "title": "Hazardous class (separate with commas)",
        "hint_detail": "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "hint_panel" : "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "example": "2.3,3.2,ORM-AIR",
        "sequence": 21,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength:10,
            required: false,
            message: 'Hazardous class is required when regulated and must be no more than 10 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousClass",
        "item_name": "PL.hazardous_materials.0.regulated.hazardous_class",
        "title": "Hazardous class (separate with commas)",
        "hint_detail": "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "hint_panel" : "If item is regulated, please use the appropriate referable Hazardous Class. May be multiple classes such as 2.3,3.2 or ORM-AIR",
        "example": "2.3,3.2,ORM-AIR",
        "sequence": 21,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength:10,
            required: false,
            message: 'Hazardous class is required when regulated and must be no more than 10 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "outerPackageLabel",
        "item_name": "EA.hazardous_materials.0.regulated.outer_package_label",
        "title": "Outer package label",
        "hint_detail": "If item is regulated, indicates the relevant Hazardous Material Class for the Outer Package Label.",
        "hint_panel" : "If item is regulated, indicate the relevant Hazardous Material Class for the Outer Package Label.",
        "example": "ORM-AIR",
        "sequence": 22,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 20,
            required: false,
            message: 'Outer package label is required when regulated and must be no more than 20 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "outerPackageLabel",
        "item_name": "PK.hazardous_materials.0.regulated.outer_package_label",
        "title": "Outer package label",
        "hint_detail": "If item is regulated, indicates the relevant Hazardous Material Class for the Outer Package Label.",
        "hint_panel" : "If item is regulated, indicate the relevant Hazardous Material Class for the Outer Package Label.",
        "example": "ORM-AIR",
        "sequence": 22,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 20,
            required: false,
            message: 'Outer package label is required when regulated and must be no more than 20 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "outerPackageLabel",
        "item_name": "BX.hazardous_materials.0.regulated.outer_package_label",
        "title": "Outer package label",
        "hint_detail": "If item is regulated, indicates the relevant Hazardous Material Class for the Outer Package Label.",
        "hint_panel" : "If item is regulated, indicate the relevant Hazardous Material Class for the Outer Package Label.",
        "example": "ORM-AIR",
        "sequence": 22,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 20,
            required: false,
            message: 'Outer package label is required when regulated and must be no more than 20 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "outerPackageLabel",
        "item_name": "CA.hazardous_materials.0.regulated.outer_package_label",
        "title": "Outer package label",
        "hint_detail": "If item is regulated, indicates the relevant Hazardous Material Class for the Outer Package Label.",
        "hint_panel" : "If item is regulated, indicate the relevant Hazardous Material Class for the Outer Package Label.",
        "example": "ORM-AIR",
        "sequence": 22,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 20,
            required: false,
            message: 'Outer package label is required when regulated and must be no more than 20 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "outerPackageLabel",
        "item_name": "PL.hazardous_materials.0.regulated.outer_package_label",
        "title": "Outer package label",
        "hint_detail": "If item is regulated, indicates the relevant Hazardous Material Class for the Outer Package Label.",
        "hint_panel" : "If item is regulated, indicate the relevant Hazardous Material Class for the Outer Package Label.",
        "example": "ORM-AIR",
        "sequence": 22,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 20,
            required: false,
            message: 'Outer package label is required when regulated and must be no more than 20 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "unnaidCode",
        "item_name": "EA.hazardous_materials.0.regulated.unnaid_code",
        "title": "UN/NA ID code",
        "hint_detail": "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "hint_panel" : "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "example": "ID8000",
        "sequence": 23,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 6,
            required: false,
            message: 'UN/NA ID is required when regulated and code must be exactly 6 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "unnaidCode",
        "item_name": "PK.hazardous_materials.0.regulated.unnaid_code",
        "title": "UN/NA ID code",
        "hint_detail": "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "hint_panel" : "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "example": "ID8000",
        "sequence": 23,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 6,
            required: false,
            message: 'UN/NA ID is required when regulated and code must be exactly 6 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "unnaidCode",
        "item_name": "BX.hazardous_materials.0.regulated.unnaid_code",
        "title": "UN/NA ID code",
        "hint_detail": "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "hint_panel" : "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "example": "ID8000",
        "sequence": 23,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 6,
            required: false,
            message: 'UN/NA ID is required when regulated and code must be exactly 6 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "unnaidCode",
        "item_name": "CA.hazardous_materials.0.regulated.unnaid_code",
        "title": "UN/NA ID code",
        "hint_detail": "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "hint_panel" : "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "example": "ID8000",
        "sequence": 23,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 6,
            required: false,
            message: 'UN/NA ID is required when regulated and code must be exactly 6 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "unnaidCode",
        "item_name": "PL.hazardous_materials.0.regulated.unnaid_code",
        "title": "UN/NA ID code",
        "hint_detail": "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "hint_panel" : "UN / NA # United Nations (UN) Numbers are four-digit numbers used to identify hazardous chemicals or classes of hazardous materials worldwide. North American (NA) Numbers are identical to UN numbers. If a material does not have a UN number, it may be assigned an NA number.",
        "example": "ID8000",
        "sequence": 23,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            length: 6,
            required: false,
            message: 'UN/NA ID is required when regulated and code must be exactly 6 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "shippingName",
        "item_name": "EA.hazardous_materials.0.regulated.shipping_name",
        "title": "Shipping name",
        "hint_detail": "Name of hazardous commodity. Can include shipping instructions.",
        "hint_panel" : "Name of hazardous commodity. Can include shipping instructions. Required if â€œRegulatedâ€ is Yes.",
        "example": "Flammable Aerosol",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 260,
            required: false,
            message: 'Shipping name is required when regulated and must be no more than 260 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "shippingName",
        "item_name": "PK.hazardous_materials.0.regulated.shipping_name",
        "title": "Shipping name",
        "hint_detail": "Name of hazardous commodity. Can include shipping instructions.",
        "hint_panel" : "Name of hazardous commodity. Can include shipping instructions. Required if â€œRegulatedâ€ is Yes.",
        "example": "Flammable Aerosol",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 260,
            required: false,
            message: 'Shipping name is required when regulated and must be no more than 260 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "shippingName",
        "item_name": "BX.hazardous_materials.0.regulated.shipping_name",
        "title": "Shipping name",
        "hint_detail": "Name of hazardous commodity. Can include shipping instructions.",
        "hint_panel" : "Name of hazardous commodity. Can include shipping instructions. Required if â€œRegulatedâ€ is Yes.",
        "example": "Flammable Aerosol",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 260,
            required: false,
            message: 'Shipping name is required when regulated and must be no more than 260 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "shippingName",
        "item_name": "CA.hazardous_materials.0.regulated.shipping_name",
        "title": "Shipping name",
        "hint_detail": "Name of hazardous commodity. Can include shipping instructions.",
        "hint_panel" : "Name of hazardous commodity. Can include shipping instructions. Required if â€œRegulatedâ€ is Yes.",
        "example": "Flammable Aerosol",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 260,
            required: false,
            message: 'Shipping name is required when regulated and must be no more than 260 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "shippingName",
        "item_name": "PL.hazardous_materials.0.regulated.shipping_name",
        "title": "Shipping name",
        "hint_detail": "Name of hazardous commodity. Can include shipping instructions.",
        "hint_panel" : "Name of hazardous commodity. Can include shipping instructions. Required if â€œRegulatedâ€ is Yes.",
        "example": "Flammable Aerosol",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 260,
            required: false,
            message: 'Shipping name is required when regulated and must be no more than 260 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "description",
        "item_name": "EA.hazardous_materials.0.regulated.description",
        "title": "Description",
        "hint_detail": "Description of the Hazardous material.",
        "hint_panel" : "Enter description of the Hazardous material.",
        "example": "Flammable Aerosol under high pressure.",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 200,
            required: false,
            message: 'Description is required when regulated and must be no more than 200 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "description",
        "item_name": "PK.hazardous_materials.0.regulated.description",
        "title": "Description",
        "hint_detail": "Description of the Hazardous material.",
        "hint_panel" : "Enter description of the Hazardous material.",
        "example": "Flammable Aerosol under high pressure.",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 200,
            required: false,
            message: 'Description is required when regulated and must be no more than 200 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "description",
        "item_name": "BX.hazardous_materials.0.regulated.description",
        "title": "Description",
        "hint_detail": "Description of the Hazardous material.",
        "hint_panel" : "Enter description of the Hazardous material.",
        "example": "Flammable Aerosol under high pressure.",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 200,
            required: false,
            message: 'Description is required when regulated and must be no more than 200 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "description",
        "item_name": "CA.hazardous_materials.0.regulated.description",
        "title": "Description",
        "hint_detail": "Description of the Hazardous material.",
        "hint_panel" : "Enter description of the Hazardous material.",
        "example": "Flammable Aerosol under high pressure.",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 200,
            required: false,
            message: 'Description is required when regulated and must be no more than 200 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "description",
        "item_name": "PL.hazardous_materials.0.regulated.description",
        "title": "Description",
        "hint_detail": "Description of the Hazardous material.",
        "hint_panel" : "Enter description of the Hazardous material.",
        "example": "Flammable Aerosol under high pressure.",
        "sequence": 24,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 200,
            required: false,
            message: 'Description is required when regulated and must be no more than 200 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousMaterialCodeQualifier",
        "item_name": "EA.hazardous_materials.0.hazardous_material_code_qualifier",
        "title": "Hazmat code qualifier",
        "hint_detail": "Department of Transport Hazardous Material Code List",
        "hint_panel" : "Department of Transport Hazardous Material Code List",
        "example": "",
        "sequence": 24,
        "show_more": true,
        "lookup": [
          {
            "option": "D",
            "title": "Department of Transport Hazardous Material Code List"
          }
        ]
    },
    {
        "field_map": "hazardousMaterialCodeQualifier",
        "item_name": "PK.hazardous_materials.0.hazardous_material_code_qualifier",
        "title": "Hazmat code qualifier",
        "hint_detail": "Department of Transport Hazardous Material Code List",
        "hint_panel" : "Department of Transport Hazardous Material Code List",
        "example": "",
        "sequence": 24,
        "show_more": true,
        "lookup": [
          {
            "option": "D",
            "title": "Department of Transport Hazardous Material Code List"
          }
        ]
    },
    {
        "field_map": "hazardousMaterialCodeQualifier",
        "item_name": "BX.hazardous_materials.0.hazardous_material_code_qualifier",
        "title": "Hazmat code qualifier",
        "hint_detail": "Department of Transport Hazardous Material Code List",
        "hint_panel" : "Department of Transport Hazardous Material Code List",
        "example": "",
        "sequence": 24,
        "show_more": true,
        "lookup": [
          {
            "option": "D",
            "title": "Department of Transport Hazardous Material Code List"
          }
        ]
    },
    {
        "field_map": "hazardousMaterialCodeQualifier",
        "item_name": "CA.hazardous_materials.0.hazardous_material_code_qualifier",
        "title": "Hazmat code qualifier",
        "hint_detail": "Department of Transport Hazardous Material Code List",
        "hint_panel" : "Department of Transport Hazardous Material Code List",
        "example": "",
        "sequence": 24,
        "show_more": true,
        "lookup": [
          {
            "option": "D",
            "title": "Department of Transport Hazardous Material Code List"
          }
        ]
    },
    {
        "field_map": "hazardousMaterialCodeQualifier",
        "item_name": "PL.hazardous_materials.0.hazardous_material_code_qualifier",
        "title": "Hazmat code qualifier",
        "hint_detail": "Department of Transport Hazardous Material Code List",
        "hint_panel" : "Department of Transport Hazardous Material Code List",
        "example": "",
        "sequence": 24,
        "show_more": true,
        "lookup": [
          {
            "option": "D",
            "title": "Department of Transport Hazardous Material Code List"
          }
        ]
    },
    {
        "field_map": "hazardousMaterialClassCode",
        "item_name": "EA.hazardous_materials.0.hazardous_material_class_code",
        "title": "HAZMAT class code",
        "hint_detail": "Hazardous materials class code",
        "hint_panel" : "Hazardous materials class code",
        "example": "Explosives projected",
        "sequence": 25,
        "show_more": true,
        "lookup": [
          {
            "option": "1.1",
            "title": "Explosives"
          },
          {
            "option": "1.2",
            "title": "Explosives projected"
          },
          {
            "option": "1.3",
            "title": "Explosives fire"
          },
          {
            "option": "1.4",
            "title": "Explosives no blast"
          },
          {
            "option": "1.5",
            "title": "Very insensitive explos"
          },
          {
            "option": "1.6",
            "title": "Extremely insensitive"
          },
          {
            "option": "2.1",
            "title": "Flammable gas"
          },
          {
            "option": "2.2",
            "title": "Non-flammable gas"
          },
          {
            "option": "2.3",
            "title": "Poisonous gas"
          },
          {
            "option": "3",
            "title": "Flammable liquid"
          },
          {
            "option": "4.1",
            "title": "Flammable solid"
          },
          {
            "option": "4.2",
            "title": "Combustible material"
          },
          {
            "option": "4.3",
            "title": "Dangerous when wet"
          },
          {
            "option": "5.1",
            "title": "Oxidizer"
          },
          {
            "option": "5.2",
            "title": "Organic peroxide"
          },
          {
            "option": "6.1",
            "title": "Poisonous Material"
          },
          {
            "option": "6.2",
            "title": "Infectious substance"
          },
          {
            "option": "7",
            "title": "Radioactive"
          },
          {
            "option": "8",
            "title": "Corrosive material"
          },
          {
            "option": "9",
            "title": "Misc. haz material"
          },
        ]
    },
    {
        "field_map": "hazardousMaterialClassCode",
        "item_name": "PK.hazardous_materials.0.hazardous_material_class_code",
        "title": "HAZMAT class code",
        "hint_detail": "Hazardous materials class code",
        "hint_panel" : "Hazardous materials class code",
        "example": "Explosives projected",
        "sequence": 25,
        "show_more": true,
        "lookup": [
          {
            "option": "1.1",
            "title": "Explosives"
          },
          {
            "option": "1.2",
            "title": "Explosives projected"
          },
          {
            "option": "1.3",
            "title": "Explosives fire"
          },
          {
            "option": "1.4",
            "title": "Explosives no blast"
          },
          {
            "option": "1.5",
            "title": "Very insensitive explos"
          },
          {
            "option": "1.6",
            "title": "Extremely insensitive"
          },
          {
            "option": "2.1",
            "title": "Flammable gas"
          },
          {
            "option": "2.2",
            "title": "Non-flammable gas"
          },
          {
            "option": "2.3",
            "title": "Poisonous gas"
          },
          {
            "option": "3",
            "title": "Flammable liquid"
          },
          {
            "option": "4.1",
            "title": "Flammable solid"
          },
          {
            "option": "4.2",
            "title": "Combustible material"
          },
          {
            "option": "4.3",
            "title": "Dangerous when wet"
          },
          {
            "option": "5.1",
            "title": "Oxidizer"
          },
          {
            "option": "5.2",
            "title": "Organic peroxide"
          },
          {
            "option": "6.1",
            "title": "Poisonous Material"
          },
          {
            "option": "6.2",
            "title": "Infectious substance"
          },
          {
            "option": "7",
            "title": "Radioactive"
          },
          {
            "option": "8",
            "title": "Corrosive material"
          },
          {
            "option": "9",
            "title": "Misc. haz material"
          },
        ]
    },
    {
        "field_map": "hazardousMaterialClassCode",
        "item_name": "BX.hazardous_materials.0.hazardous_material_class_code",
        "title": "HAZMAT class code",
        "hint_detail": "Hazardous materials class code",
        "hint_panel" : "Hazardous materials class code",
        "example": "Explosives projected",
        "sequence": 25,
        "show_more": true,
        "lookup": [
          {
            "option": "1.1",
            "title": "Explosives"
          },
          {
            "option": "1.2",
            "title": "Explosives projected"
          },
          {
            "option": "1.3",
            "title": "Explosives fire"
          },
          {
            "option": "1.4",
            "title": "Explosives no blast"
          },
          {
            "option": "1.5",
            "title": "Very insensitive explos"
          },
          {
            "option": "1.6",
            "title": "Extremely insensitive"
          },
          {
            "option": "2.1",
            "title": "Flammable gas"
          },
          {
            "option": "2.2",
            "title": "Non-flammable gas"
          },
          {
            "option": "2.3",
            "title": "Poisonous gas"
          },
          {
            "option": "3",
            "title": "Flammable liquid"
          },
          {
            "option": "4.1",
            "title": "Flammable solid"
          },
          {
            "option": "4.2",
            "title": "Combustible material"
          },
          {
            "option": "4.3",
            "title": "Dangerous when wet"
          },
          {
            "option": "5.1",
            "title": "Oxidizer"
          },
          {
            "option": "5.2",
            "title": "Organic peroxide"
          },
          {
            "option": "6.1",
            "title": "Poisonous Material"
          },
          {
            "option": "6.2",
            "title": "Infectious substance"
          },
          {
            "option": "7",
            "title": "Radioactive"
          },
          {
            "option": "8",
            "title": "Corrosive material"
          },
          {
            "option": "9",
            "title": "Misc. haz material"
          },
        ]
    },
    {
        "field_map": "hazardousMaterialClassCode",
        "item_name": "CA.hazardous_materials.0.hazardous_material_class_code",
        "title": "HAZMAT class code",
        "hint_detail": "Hazardous materials class code",
        "hint_panel" : "Hazardous materials class code",
        "example": "Explosives projected",
        "sequence": 25,
        "show_more": true,
        "lookup": [
          {
            "option": "1.1",
            "title": "Explosives"
          },
          {
            "option": "1.2",
            "title": "Explosives projected"
          },
          {
            "option": "1.3",
            "title": "Explosives fire"
          },
          {
            "option": "1.4",
            "title": "Explosives no blast"
          },
          {
            "option": "1.5",
            "title": "Very insensitive explos"
          },
          {
            "option": "1.6",
            "title": "Extremely insensitive"
          },
          {
            "option": "2.1",
            "title": "Flammable gas"
          },
          {
            "option": "2.2",
            "title": "Non-flammable gas"
          },
          {
            "option": "2.3",
            "title": "Poisonous gas"
          },
          {
            "option": "3",
            "title": "Flammable liquid"
          },
          {
            "option": "4.1",
            "title": "Flammable solid"
          },
          {
            "option": "4.2",
            "title": "Combustible material"
          },
          {
            "option": "4.3",
            "title": "Dangerous when wet"
          },
          {
            "option": "5.1",
            "title": "Oxidizer"
          },
          {
            "option": "5.2",
            "title": "Organic peroxide"
          },
          {
            "option": "6.1",
            "title": "Poisonous Material"
          },
          {
            "option": "6.2",
            "title": "Infectious substance"
          },
          {
            "option": "7",
            "title": "Radioactive"
          },
          {
            "option": "8",
            "title": "Corrosive material"
          },
          {
            "option": "9",
            "title": "Misc. haz material"
          },
        ]
    },
    {
        "field_map": "hazardousMaterialClassCode",
        "item_name": "PL.hazardous_materials.0.hazardous_material_class_code",
        "title": "HAZMAT class code",
        "hint_detail": "Hazardous materials class code",
        "hint_panel" : "Hazardous materials class code",
        "example": "Explosives projected",
        "sequence": 25,
        "show_more": true,
        "lookup": [
          {
            "option": "1.1",
            "title": "Explosives"
          },
          {
            "option": "1.2",
            "title": "Explosives projected"
          },
          {
            "option": "1.3",
            "title": "Explosives fire"
          },
          {
            "option": "1.4",
            "title": "Explosives no blast"
          },
          {
            "option": "1.5",
            "title": "Very insensitive explos"
          },
          {
            "option": "1.6",
            "title": "Extremely insensitive"
          },
          {
            "option": "2.1",
            "title": "Flammable gas"
          },
          {
            "option": "2.2",
            "title": "Non-flammable gas"
          },
          {
            "option": "2.3",
            "title": "Poisonous gas"
          },
          {
            "option": "3",
            "title": "Flammable liquid"
          },
          {
            "option": "4.1",
            "title": "Flammable solid"
          },
          {
            "option": "4.2",
            "title": "Combustible material"
          },
          {
            "option": "4.3",
            "title": "Dangerous when wet"
          },
          {
            "option": "5.1",
            "title": "Oxidizer"
          },
          {
            "option": "5.2",
            "title": "Organic peroxide"
          },
          {
            "option": "6.1",
            "title": "Poisonous Material"
          },
          {
            "option": "6.2",
            "title": "Infectious substance"
          },
          {
            "option": "7",
            "title": "Radioactive"
          },
          {
            "option": "8",
            "title": "Corrosive material"
          },
          {
            "option": "9",
            "title": "Misc. haz material"
          },
        ]
    },
    {
        "field_map": "hazardousMaterialDescription",
        "item_name": "EA.hazardous_materials.0.hazardous_material_description",
        "title": "HAZMAT description",
        "hint_detail": "HAZMAT description",
        "hint_panel" : "HAZMAT description",
        "example": "HAZMAT description of the class code.",
        "sequence": 25,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: 'HAZMAT description must be no more than 80 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousMaterialDescription",
        "item_name": "PK.hazardous_materials.0.hazardous_material_description",
        "title": "HAZMAT description",
        "hint_detail": "HAZMAT description",
        "hint_panel" : "HAZMAT description",
        "example": "HAZMAT description of the class code.",
        "sequence": 25,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: 'HAZMAT description must be no more than 80 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousMaterialDescription",
        "item_name": "BX.hazardous_materials.0.hazardous_material_description",
        "title": "HAZMAT description",
        "hint_detail": "HAZMAT description",
        "hint_panel" : "HAZMAT description",
        "example": "HAZMAT description of the class code.",
        "sequence": 25,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: 'HAZMAT description must be no more than 80 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousMaterialDescription",
        "item_name": "CA.hazardous_materials.0.hazardous_material_description",
        "title": "HAZMAT description",
        "hint_detail": "HAZMAT description",
        "hint_panel" : "HAZMAT description",
        "example": "HAZMAT description of the class code.",
        "sequence": 25,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: 'HAZMAT description must be no more than 80 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousMaterialDescription",
        "item_name": "PL.hazardous_materials.0.hazardous_material_description",
        "title": "HAZMAT description",
        "hint_detail": "HAZMAT description",
        "hint_panel" : "HAZMAT description",
        "example": "HAZMAT description of the class code.",
        "sequence": 25,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: 'HAZMAT description must be no more than 80 characters.'
          }
        },
        "lookup": null
    },
    {
        "field_map": "whmisCode",
        "item_name": "EA.hazardous_materials.0.whmis_code",
        "title": "WHMIS code",
        "hint_detail": "WHMIS code",
        "hint_panel" : "WHMIS code",
        "example": "Flammable gas",
        "sequence": 26,
        "show_more": true,
        "lookup": whmisCodeLookup,
    },
    {
        "field_map": "whmisCode",
        "item_name": "PK.hazardous_materials.0.whmis_code",
        "title": "WHMIS code",
        "hint_detail": "WHMIS code",
        "hint_panel" : "WHMIS code",
        "example": "Flammable gas",
        "sequence": 26,
        "show_more": true,
        "lookup": whmisCodeLookup,
    },
    {
        "field_map": "whmisCode",
        "item_name": "BX.hazardous_materials.0.whmis_code",
        "title": "WHMIS code",
        "hint_detail": "WHMIS code",
        "hint_panel" : "WHMIS code",
        "example": "Flammable gas",
        "sequence": 26,
        "show_more": true,
        "lookup": whmisCodeLookup,
    },
    {
        "field_map": "whmisCode",
        "item_name": "CA.hazardous_materials.0.whmis_code",
        "title": "WHMIS code",
        "hint_detail": "WHMIS code",
        "hint_panel" : "WHMIS code",
        "example": "Flammable gas",
        "sequence": 26,
        "show_more": true,
        "lookup": whmisCodeLookup,
    },
    {
        "field_map": "whmisCode",
        "item_name": "PL.hazardous_materials.0.whmis_code",
        "title": "WHMIS code",
        "hint_detail": "WHMIS code",
        "hint_panel" : "WHMIS code",
        "example": "Flammable gas",
        "sequence": 26,
        "show_more": true,
        "lookup": whmisCodeLookup,
    },
    {
        "field_map": "whmisFreeText",
        "item_name": "EA.hazardous_materials.0.whmis_free_text",
        "title": "WHMIS description",
        "hint_detail": "WHMIS description",
        "hint_panel" : "WHMIS description",
        "example": "",
        "sequence": 27,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: "WHMIS description must be no more than 80 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "whmisFreeText",
        "item_name": "PK.hazardous_materials.0.whmis_free_text",
        "title": "WHMIS description",
        "hint_detail": "WHMIS description",
        "hint_panel" : "WHMIS description",
        "example": "",
        "sequence": 27,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: "WHMIS description must be no more than 80 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "whmisFreeText",
        "item_name": "BX.hazardous_materials.0.whmis_free_text",
        "title": "WHMIS description",
        "hint_detail": "WHMIS description",
        "hint_panel" : "WHMIS description",
        "example": "",
        "sequence": 27,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: "WHMIS description must be no more than 80 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "whmisFreeText",
        "item_name": "CA.hazardous_materials.0.whmis_free_text",
        "title": "WHMIS description",
        "hint_detail": "WHMIS description",
        "hint_panel" : "WHMIS description",
        "example": "",
        "sequence": 27,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: "WHMIS description must be no more than 80 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "whmisFreeText",
        "item_name": "PL.hazardous_materials.0.whmis_free_text",
        "title": "WHMIS description",
        "hint_detail": "WHMIS description",
        "hint_panel" : "WHMIS description",
        "example": "",
        "sequence": 27,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 80,
            message: "WHMIS description must be no more than 80 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "packingGroupCode",
        "item_name": "EA.hazardous_materials.0.packing_group_code",
        "title": "Packing group code",
        "hint_detail": "Indicates the danger level for this hazardous material",
        "hint_panel" : "Select the danger level for this hazardous material",
        "example": "Great danger",
        "sequence": 28,
        "show_more": true,
        "lookup": [
          {
            "option": "I",
            "title": "Great danger"
          },
          {
            "option": "II",
            "title": "Medium Danger"
          },
          {
            "option": "III",
            "title": "Low Danger"
          }
        ]
    },
    {
        "field_map": "packingGroupCode",
        "item_name": "PK.hazardous_materials.0.packing_group_code",
        "title": "Packing group code",
        "hint_detail": "Indicates the danger level for this hazardous material",
        "hint_panel" : "Select the danger level for this hazardous material",
        "example": "Great danger",
        "sequence": 28,
        "show_more": true,
        "lookup": [
          {
            "option": "I",
            "title": "Great danger"
          },
          {
            "option": "II",
            "title": "Medium Danger"
          },
          {
            "option": "III",
            "title": "Low Danger"
          }
        ]
    },
    {
        "field_map": "packingGroupCode",
        "item_name": "BX.hazardous_materials.0.packing_group_code",
        "title": "Packing group code",
        "hint_detail": "Indicates the danger level for this hazardous material",
        "hint_panel" : "Select the danger level for this hazardous material",
        "example": "Great danger",
        "sequence": 28,
        "show_more": true,
        "lookup": [
          {
            "option": "I",
            "title": "Great danger"
          },
          {
            "option": "II",
            "title": "Medium Danger"
          },
          {
            "option": "III",
            "title": "Low Danger"
          }
        ]
    },
    {
        "field_map": "packingGroupCode",
        "item_name": "CA.hazardous_materials.0.packing_group_code",
        "title": "Packing group code",
        "hint_detail": "Indicates the danger level for this hazardous material",
        "hint_panel" : "Select the danger level for this hazardous material",
        "example": "Great danger",
        "sequence": 28,
        "show_more": true,
        "lookup": [
          {
            "option": "I",
            "title": "Great danger"
          },
          {
            "option": "II",
            "title": "Medium Danger"
          },
          {
            "option": "III",
            "title": "Low Danger"
          }
        ]
    },
    {
        "field_map": "packingGroupCode",
        "item_name": "PL.hazardous_materials.0.packing_group_code",
        "title": "Packing group code",
        "hint_detail": "Indicates the danger level for this hazardous material",
        "hint_panel" : "Select the danger level for this hazardous material",
        "example": "Great danger",
        "sequence": 28,
        "show_more": true,
        "lookup": [
          {
            "option": "I",
            "title": "Great danger"
          },
          {
            "option": "II",
            "title": "Medium danger"
          },
          {
            "option": "III",
            "title": "Low danger"
          }
        ]
    },
    {
        "field_map": "regulationsExemptionCode",
        "item_name": "EA.hazardous_materials.0.regulations_exemption_code",
        "title": "Regulations exemption code",
        "hint_detail": "Regulations exemption code",
        "hint_panel" : "Regulations exemption code",
        "example": "ABCD",
        "sequence": 29,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 4,
            message: "Regulations exemption code must be no more than 4 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "regulationsExemptionCode",
        "item_name": "PK.hazardous_materials.0.regulations_exemption_code",
        "title": "Regulations exemption code",
        "hint_detail": "Regulations exemption code",
        "hint_panel" : "Regulations exemption code",
        "example": "ABCD",
        "sequence": 29,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 4,
            message: "Regulations exemption code must be no more than 4 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "regulationsExemptionCode",
        "item_name": "BX.hazardous_materials.0.regulations_exemption_code",
        "title": "Regulations exemption code",
        "hint_detail": "Regulations exemption code",
        "hint_panel" : "Regulations exemption code",
        "example": "ABCD",
        "sequence": 29,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 4,
            message: "Regulations exemption code must be no more than 4 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "regulationsExemptionCode",
        "item_name": "CA.hazardous_materials.0.regulations_exemption_code",
        "title": "Regulations exemption code",
        "hint_detail": "Regulations exemption code",
        "hint_panel" : "Regulations exemption code",
        "example": "ABCD",
        "sequence": 29,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 4,
            message: "Regulations exemption code must be no more than 4 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "regulationsExemptionCode",
        "item_name": "PL.hazardous_materials.0.regulations_exemption_code",
        "title": "Regulations exemption code",
        "hint_detail": "Regulations exemption code",
        "hint_panel" : "Regulations exemption code",
        "example": "ABCD",
        "sequence": 29,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 4,
            message: "Regulations exemption code must be no more than 4 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousPlacardNotation",
        "item_name": "EA.hazardous_materials.0.hazardous_placard_notation",
        "title": "Placard notation",
        "hint_detail": "Placard notation",
        "hint_panel" : "Placard notation",
        "example": "",
        "sequence": 30,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 40,
            message: "Placard notation must be no more than 40 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousPlacardNotation",
        "item_name": "PK.hazardous_materials.0.hazardous_placard_notation",
        "title": "Placard notation",
        "hint_detail": "Placard notation",
        "hint_panel" : "Placard notation",
        "example": "",
        "sequence": 30,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 40,
            message: "Placard notation must be no more than 40 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousPlacardNotation",
        "item_name": "BX.hazardous_materials.0.hazardous_placard_notation",
        "title": "Placard notation",
        "hint_detail": "Placard notation",
        "hint_panel" : "Placard notation",
        "example": "",
        "sequence": 30,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 40,
            message: "Placard notation must be no more than 40 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousPlacardNotation",
        "item_name": "CA.hazardous_materials.0.hazardous_placard_notation",
        "title": "Placard notation",
        "hint_detail": "Placard notation",
        "hint_panel" : "Placard notation",
        "example": "",
        "sequence": 30,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 40,
            message: "Placard notation must be no more than 40 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "hazardousPlacardNotation",
        "item_name": "PL.hazardous_materials.0.hazardous_placard_notation",
        "title": "Placard notation",
        "hint_detail": "Placard notation",
        "hint_panel" : "Placard notation",
        "example": "",
        "sequence": 30,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 40,
            message: "Placard notation must be no more than 40 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "textMessage",
        "item_name": "EA.hazardous_materials.0.text_message",
        "title": "Text message",
        "hint_detail": "Text message",
        "hint_panel" : "Text message",
        "example": "",
        "sequence": 31,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 2000,
            message: "Text message must be no more than 2000 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "textMessage",
        "item_name": "PK.hazardous_materials.0.text_message",
        "title": "Text message",
        "hint_detail": "Text message",
        "hint_panel" : "Text message",
        "example": "",
        "sequence": 31,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 2000,
            message: "Text message must be no more than 2000 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "textMessage",
        "item_name": "BX.hazardous_materials.0.text_message",
        "title": "Text message",
        "hint_detail": "Text message",
        "hint_panel" : "Text message",
        "example": "",
        "sequence": 31,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 2000,
            message: "Text message must be no more than 2000 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "textMessage",
        "item_name": "CA.hazardous_materials.0.text_message",
        "title": "Text message",
        "hint_detail": "Text message",
        "hint_panel" : "Text message",
        "example": "",
        "sequence": 31,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 2000,
            message: "Text message must be no more than 2000 characters."
          }
        },
        "lookup": null
    },
    {
        "field_map": "textMessage",
        "item_name": "PL.hazardous_materials.0.text_message",
        "title": "Text message",
        "hint_detail": "Text message",
        "hint_panel" : "Text message",
        "example": "",
        "sequence": 31,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 2000,
            message: "Text message must be no more than 2000 characters."
          }
        },
        "lookup": null
    }
  ],
  "extendedInformation": [
    {
        "field_map": "msr",
        "item_name": "MSR.EN",
        "title": "MSDS required",
        "hint_detail": "Is the MSDS sheet required? Yes or no.",
        "hint_panel" : "Is the MSDS sheet required? Yes or no.",
        "example": "Yes",
        "sequence": 1,
        "show_more": true,
        "lookup": yesNoLookup,
        'validation': {
          'PIES_6_5' : {
            'custom': function (): ValidatorFn {
              return isLookupOption(yesNoLookup);
            },
            message: 'Code doesn\'t exist within the lookup.'
          }
        },
    },
    {
        "field_map": "msd",
        "item_name": "MSD.EN",
        "title": "MSDS sheet number",
        "hint_detail": "MSDS sheet number",
        "hint_panel" : "MSDS sheet number",
        "example": "",
        "sequence": 2,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 35,
            message: "MSDS sheet number must be no more than 35 characters."
          }
        },
    },
    {
        "field_map": "cto",
        "item_name": "CTO.EN",
        "title": "Country of origin (primary)",
        "hint_detail": "Primary country of origin using ISO 2166 country codes.",
        "hint_panel" : "Choose from the ISO 3166 country codes to list the country of origin.",
        "example": "United States",
        "sequence": 3,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "hsb",
        "item_name": "HSB.EN",
        "title": "Harmonized tariff code (Schedule B)",
        "hint_detail": "A Schedule B number is a 10-digit number used in the United States to classify physical goods for export to another country. The Schedule B is based on the international Harmonized System (HS) of 6-digit commodity classification codes.",
        "hint_panel" : "A Schedule B number is a 10-digit number used in the United States to classify physical goods for export to another country. The Schedule B is based on the international Harmonized System (HS) of 6-digit commodity classification codes.",
        "example": "0987654231",
        "sequence": 4,
        "show_more": true,
        "lookup": null,
        "numerical": true,
        'validation': {
          'PIES_6_5' : {
            'length': 10,
            'message': 'Harmonized tariff code must be exactly 10 characters.'
          }
        },
    },
    {
        "field_map": "sta",
        "item_name": "STA.EN",
        "title": "Stock status",
        "hint_detail": "Stock status of the item",
        "hint_panel" : "Select the stock status",
        "example": "Made to order",
        "sequence": 5,
        "show_more": true,
        "lookup": [
          {
            "option": "S",
            "title": "Shelf"
          },
          {
            "option": "N",
            "title": "Made to order"
          },
          {
            "option": "Q",
            "title": "Quick-ship"
          }
        ]
    },
    {
        "field_map": "plc",
        "item_name": "PLC.EN",
        "title": "Maximum cases per pallet",
        "hint_detail": "Maximum allowable cases per pallet layer for this item.",
        "hint_panel" : "Enter the maximum allowable cases per pallet layer for this item.",
        "example": "18",
        "sequence": 6,
        "show_more": true,
        "lookup": null,
        'numerical': true,
        'validation': {
          'PIES_6_5' : {
            min: 1,
            max: 3,
            message: "Maximum cases per pallet cannot be greater than 3."
          }
        },
    },
    {
        "field_map": "rst",
        "item_name": "RST.EN",
        "title": "Regulating state",
        "hint_detail": "Regulating state",
        "hint_panel" : "Regulating state",
        "example": "New York",
        "sequence": 7,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            length: 2,
            message: 'Regulating state must be exactly 2 characters.'
          }
        },
    },
    {
        "field_map": "rpc",
        "item_name": "RPC.EN",
        "title": "Regulating postal code",
        "hint_detail": "Regulating postal code",
        "hint_panel" : "Regulating postal code",
        "example": "00000",
        "sequence": 8,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            minLength: 5,
            maxLength: 10,
            message: "Regulating postal code must be between 5 and 10 characters."
          }
        },
    },
    {
        "field_map": "rcs",
        "item_name": "RCS.EN",
        "title": "Regulating country, state",
        "hint_detail": "Regulating country, state",
        "hint_panel" : "Regulating country, state",
        "example": "United States, New York",
        "sequence": 9,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 50,
            message: "Regulating country, state must be no more than 50 characters."
          }
        },
    },
    {
        "field_map": "rct",
        "item_name": "RCT.EN",
        "title": "Regulating city, state",
        "hint_detail": "Regulating city, state",
        "hint_panel" : "Regulating city, state",
        "example": "New York City, New York",
        "sequence": 10,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 50,
            message: "Regulating city, state must be no more than 50 characters."
          }
        },
    },
    {
        "field_map": "ctp",
        "item_name": "CTP.EN",
        "title": "Country of origin 2",
        "hint_detail": "Country of origin 2",
        "hint_panel" : "Country of origin 2",
        "example": "Belgium",
        "sequence": 11,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "ctq",
        "item_name": "CTQ.EN",
        "title": "Country of origin 3",
        "hint_detail": "Country of origin 3",
        "hint_panel" : "Country of origin 3",
        "example": "Russian Federation",
        "sequence": 12,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "ctr",
        "item_name": "CTR.EN",
        "title": "Country of origin 4",
        "hint_detail": "Country of origin 4",
        "hint_panel" : "Country of origin 4",
        "example": "Greenland",
        "sequence": 13,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "cts",
        "item_name": "CTS.EN",
        "title": "Country of origin 5",
        "hint_detail": "Country of origin 5",
        "hint_panel" : "Country of origin 5",
        "example": "Luxembourg",
        "sequence": 14,
        "show_more": true,
        "lookup": countryLookup,
    },
    {
        "field_map": "hac",
        "item_name": "HAC.EN",
        "title": "Canadian harmonizing tariff code",
        "hint_detail": "Canadian harmonizing tariff code",
        "hint_panel" : "Canadian harmonizing tariff code",
        "sequence": 15,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            minLength: 10,
            maxLength: 48,
            message: 'Canadian harmonizing tariff code must be between 10 and 48 characters.'
          }
        },
    },
    {
        "field_map": "hts",
        "item_name": "HTS.EN",
        "title": "Harmonized tariff code (HTS)",
        "hint_detail": "Harmonized tariff code (HTS)",
        "hint_panel" : "Harmonized tariff code (HTS)",
        "example": "4016931010",
        "sequence": 16,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            length: 10,
            message: 'Harmonized tariff code must be exactly 10 characters.'
          }
        },
    },
    {
        "field_map": "hz1",
        "item_name": "HZ1.EN",
        "title": "SKU-level special handling code",
        "hint_detail": "SKU-level special handling code",
        "hint_panel" : "SKU-level special handling code",
        "example": "",
        "sequence": 17,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            minLength: 2,
            maxLength: 3,
            message: 'SKU-level special handling code code must be between 2 and 3 characters.'
          }
        },
    },
    {
        "field_map": "naf",
        "item_name": "NAF.EN",
        "title": "NAFTA preference criterion code",
        "hint_detail": "NAFTA preference criterion code",
        "hint_panel" : "NAFTA preference criterion code",
        "example": "",
        "sequence": 18,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            length: 1,
            message: 'NAFTA preference criterion code be exactly 1 character.'
          }
        },
    },
    {
        "field_map": "tmc",
        "item_name": "TMC.EN",
        "title": "Trading partner movement code",
        "hint_detail": "Trading partner movement code",
        "hint_panel" : "Trading partner movement code",
        "example": "",
        "sequence": 19,
        "show_more": true,
        "lookup": null,
        'validation': {
          'PIES_6_5' : {
            length: 2,
            message: 'Trading partner movement code must be exactly 2 characters.'
          }
        },
    },
    {
        "field_map": "plm",
        "item_name": "PLM.EN",
        "title": "Pallet layer maximum",
        "hint_detail": "Maximum allowable layers per pallet for this item.",
        "hint_panel" : "Enter the maximum allowable layers per pallet for this item.",
        "example": "2",
        "sequence": 20,
        "show_more": true,
        'validation': {
          'PIES_6_5' : {
            max: 999,
            message: "Pallet layer maximum cannot be greater than 999"
          }
        },
        "lookup": null
    },
  ],
  "kits": [
    {
      'field_map': 'componentBrand',
      'item_name': 'component_brand',
      'title': 'Brand',
      'hint_detail': 'Brand that manufactures this kit component.',
      'hint_panel' : 'Select Brand that manufactures this kit component.',
      'example': 'ABC Aftermarket Company',
      'sequence': '',
      'show_more': true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 60,
            message: "Brand cannot be longer than 60 characters."
          }
        },
      'lookup': null
    },
    {
      'item_name': 'component_brand_label',
      'title': 'Brand',
      'hint_detail': 'Brand that manufactures this kit component.',
      'hint_panel' : 'Select Brand that manufactures this kit component.',
      'example': '',
      'sequence': '',
      'show_more': true,
        'validation': {
          'PIES_6_5' : {
            minLength: 1,
            maxLength: 60,
            message: "Brand cannot be longer than 60 characters."
          }
        },
      'lookup': null
    },
    {
      'item_name': 'part_terminology_id',
      'title': 'Part terminology ID',
      'hint_detail': '',
      'hint_panel' : 'The terminology for the part',
      'example': '',
      'sequence': '',
      'show_more': true,
      'lookup': null
    },
    {
      'field_map': 'partType',
      'item_name': 'part_type',
      'title': 'Part type',
      'hint_detail': '',
      'hint_panel' : 'The part terminology name',
      'example': '',
      'sequence': '',
      'show_more': true,
      'lookup': null
    },
    {
      "field_map": "componentPartNumber",
      'item_name': 'component_part_number.value',
      'title': 'Part number',
      'hint_detail': 'Part number of this component.',
      'hint_panel' : 'Select an existing part number, or enter a new part number to create a new item. ',
      'example': 'Multiple components make up a kit, each component must be an existing item number in your catalog but doesn\'t necessarily need to be available for sale (Not Orderable)',
      'sequence': 1,
      'show_more': true,
      'lookup': null,
    },
    {
      "field_map": "description",
      "item_name": "description.value",
      "title": "Description",
      "hint_detail": "Describes the product.",
      "hint_panel" : "Up to 20 characters briefly describing product.",
      "example": "Cold Air Intake Kit",
      "sequence": 2,
      "show_more": true,
      "lookup": null
    },
    {
      "field_map": "descriptionLanguage",
      'item_name': 'description.language',
      'title': 'Description language',
      'hint_detail': '',
      'hint_panel' : 'Select the language of the description',
      'example': '',
      'sequence': '',
      'show_more': true,
      'lookup': null,
    },
    {
      'field_map': 'descriptionType',
      'item_name': 'description.type',
      'title': 'Description type',
      'hint_detail': '',
      'hint_panel' : 'Select the description type',
      'example': '',
      'sequence': '',
      'show_more': true,
      'lookup': null,
    },
    {
      "field_map": "quantityInKit",
      'item_name': 'quantity_in_kit.quantity',
      'title': 'Quantity',
      'hint_detail': 'Quantity in kit',
      'hint_panel' : 'Enter the quantity of these components in this kit.',
      'example': '',
      'sequence': 3,
      'show_more': true,
      'numerical': true,
      'lookup': null,
      'validation': {
        'PIES_6_5' : {
          min: 1,
          max: 99999999,
          message: 'Quantity in kit cannot be greater than 99999999',
        }
      }
    },
    {
      "field_map": "quantityInKitUom",
      'item_name': 'quantity_in_kit.uom',
      'title': '',
      'hint_detail': '',
      'hint_panel' : 'Unit of measure for the packaged component in this kit.',
      'example': '',
      'sequence': 4,
      'show_more': true,
      'validation': {
        'PIES_6_5' : {
          required: true,
          minLength: 1,
          maxLength: 4
        }
      },
      "lookup": [
          { 'option': '', 'title': '-' },
          { 'option': 'OZ', 'title': 'Ounces' },
          { 'option': 'AS', 'title': 'Assortment' },
          { 'option': 'BG', 'title': 'Bag' },
          { 'option': 'BK', 'title': 'Book' },
          { 'option': 'BO', 'title': 'Bottle' },
          { 'option': 'BX', 'title': 'Box' },
          { 'option': 'BC', 'title': 'Bucket' },
          { 'option': 'BD', 'title': 'Bundle' },
          { 'option': 'CN', 'title': 'Can' },
          { 'option': 'CG', 'title': 'Card' },
          { 'option': 'CT', 'title': 'Carton' },
          { 'option': 'CQ', 'title': 'Cartridge' },
          { 'option': 'CA', 'title': 'Case' },
          { 'option': 'CX', 'title': 'Coil' },
          { 'option': 'CH', 'title': 'Container' },
          { 'option': 'FN', 'title': 'Clamshell' },
          { 'option': 'CP', 'title': 'Crate' },
          { 'option': 'DZ', 'title': 'Dozen' },
          { 'option': 'DR', 'title': 'Drum' },
          { 'option': 'EA', 'title': 'Each' },
          { 'option': 'EV', 'title': 'Envelope' },
          { 'option': 'FT', 'title': 'Foot' },
          { 'option': 'GA', 'title': 'Gallon' },
          { 'option': 'HU', 'title': 'Hundred' },
          { 'option': 'HC', 'title': 'Hundred count' },
          { 'option': 'JR', 'title': 'Jar' },
          { 'option': 'KE', 'title': 'Keg' },
          { 'option': 'KG', 'title': 'Kilogram' },
          { 'option': 'KT', 'title': 'Kit' },
          { 'option': 'LT', 'title': 'Liter' },
          { 'option': 'MR', 'title': 'Meter' },
          { 'option': 'PH', 'title': 'Pack (PAK)' },
          { 'option': 'PK', 'title': 'Package' },
          { 'option': 'PA', 'title': 'Pail' },
          { 'option': 'PR', 'title': 'Pair' },
          { 'option': 'PF', 'title': 'Pallet' },
          { 'option': 'PL', 'title': 'Pallet unit load' },
          { 'option': 'PC', 'title': 'Piece' },
          { 'option': 'PT', 'title': 'Pint' },
          { 'option': 'LB', 'title': 'Pound' },
          { 'option': 'QT', 'title': 'Quart' },
          { 'option': 'RE', 'title': 'Reel' },
          { 'option': 'RD', 'title': 'Rod' },
          { 'option': 'RL', 'title': 'Roll' },
          { 'option': 'ST', 'title': 'Set' },
          { 'option': 'SH', 'title': 'Sheet' },
          { 'option': 'SL', 'title': 'Sleeve' },
          { 'option': 'SO', 'title': 'Spool' },
          { 'option': '15', 'title': 'Stick' },
          { 'option': 'TH', 'title': 'Thousand' },
          { 'option': 'TY', 'title': 'Tray' },
          { 'option': 'TB', 'title': 'Tube' },
          { 'option': 'OP', 'title': 'Two pack' },
      ]
    },
    {
      "field_map": "soldSeparately",
      'item_name': 'sold_separately',
      'title': 'Sold separately',
      'hint_detail': 'Sold separately',
      'hint_panel' : 'Sold separately',
      'example': '',
      'sequence': 5,
      'show_more': true,
      'lookup': [
        {
          "option": "Y",
          "title": "Yes"
        }
      ]
    }
  ],
  "interchanges":[
    {
      'field_map': 'brandLabel',
      'item_name': 'brand_label',
      'title': 'Brand',
      'hint_detail': 'Brand from the AAIA Brand Table',
      'hint_panel' : 'Select an existing brand or add a new one',
      'example': '',
      'sequence': 1,
      'show_more': true,
      'validation': {
        'PIES_6_5' : {
          maxlength: 60,
          message: 'Brand must be no more than 60 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'brandId',
      'item_name': 'brand_AAIAID',
      'title': 'Brand',
      'hint_detail': 'Brand',
      'hint_panel' : 'Select an existing brand or add a new one',
      'example': '',
      'details_hide': true,
      'sequence': 1,
      'show_more': true,
      'validation': {
        'PIES_6_5' : {
          maxlength: 60,
          message: 'Brand must be no more than 60 characters.'
        }
      },
      'lookup': null,
      'api_lookup': {
          'type': 'typeahead',
          'field_map': 'brandLabel',
          'item_name': 'brand_label',
          'lookup': 'brands',
      }
    },
    {
      'field_map': 'partNumber',
      'item_name': 'part_number',
      'title': 'Part no.',
      'hint_detail': 'Part number of interchange item.',
      'hint_panel' : 'Select an existing part number from this brand',
      'example': '',
      'sequence': 2,
      'show_more': true,
      'validation': {
        'PIES_6_5' : {
          minLength: 1,
          maxLength: 48,
          message: 'Part number must be no more than 48 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'typeCode',
      'item_name': 'type_code',
      'title': 'Interchange type',
      'hint_detail': '[OEM] OEM part number [SUB] Substitute will interchange with other supplier product. [USER] User specified- code used when other\'s do not apply.',
      'hint_panel' : '[OEM] OEM part number [SUB] Substitute will interchange with other supplier product. [USER] User specified- enter a code when other\'s do not apply.',
      'example': 'Substitute',
      'sequence': 3,
      'show_more': true,
      'lookup': [
        {
          "option": "O",
          "title": "OEM"
        },
        {
          "option": "S",
          "title": "Substitute"
        },
        {
          "option": "U",
          "title": "User defined"
        },
      ]
    },
    {
      'field_map': 'qualityGradeLevel',
      'item_name': 'quality_grade_level',
      'title': 'Interchange quality',
      'hint_detail': 'Interchange quality of item.',
      'hint_panel' : 'Is this interchange product OEM, premium or standard?',
      'example': 'Standard',
      'sequence': 4,
      'show_more': true,
      'lookup': [
        {
          "option": "P",
          "title": "Premium"
        },
        {
          "option": "O",
          "title": "OEM"
        },
        {
          "option": "S",
          "title": "Standard"
        }
      ]
    },
    {
      'field_map': 'languageCode',
      'item_name': 'language',
      'title': 'Language',
      'hint_detail': 'Language in which this description is written.',
      'hint_panel' : 'Choose the language which this description is written.',
      'example': 'English',
      'sequence': 5,
      'details_hide': true,
      'show_more': true,
      'lookup': [
        {
          "option": "EN",
          "title": "English"
        },
        {
          "option": "ES",
          "title": "Spanish"
        },
        {
          "option": "FR",
          "title": "French"
        }
      ]
    },
    {
      'field_map': 'interchangeNotes',
      'item_name': 'interchange_notes',
      'title': 'Interchange notes',
      'hint_detail': 'Notes for this Interchange, up to 240 characters.',
      'hint_panel' : 'Up to 240 characters. Enter notes about this interchange.',
      'example': 'This is a Standard Substitute for the item.',
      'sequence': 6,
      'show_more': false,
      'validation': {
        'PIES_6_5' : {
          minLength: 1,
          maxLength: 240,
          message: 'Interchange notes must be no more than 240 characters.'
        }
      },
      'lookup': null
    },
    {
      'field_map': 'internalNotes',
      'item_name': 'internal_notes',
      'title': 'Internal notes',
      'hint_detail': 'Internal notes for this interchange.',
      'hint_panel' : 'Internal notes for this interchange.',
      'example': 'Notes for the internal team about this interchange.',
      'sequence': 7,
      'show_more': false,
      'validation': {
        'PIES_6_5' : {
          minLength: 1,
          maxLength: 240,
          message: 'Internal notes must be no more than 240 characters.'
        }
      },
      'lookup': null
    }
  ],
  "digitalAssets": digitalAssetsSegmentData,
  "fitments": [
    {
      // 'item_name': 'app.baseVehicle.year',
      'item_name': 'year',
      'title': 'Year',
      'hint_detail': 'Vehicle year',
      'hint_panel' : '',
      'example': '',
      'sequence': 1,
      'show_more': true,
      'lookup': null,
      'sortCol': 'year',
    },
    {
      // 'item_name': 'app.baseVehicle.make.name',
      'item_name': 'make',
      'title': 'Make',
      'hint_detail': 'Vehicle make',
      'hint_panel' : '',
      'example': '',
      'sequence': 2,
      'show_more': true,
      'lookup': null,
      'sortCol': 'make',
    },
    {
      // 'item_name': 'app.baseVehicle.model.name',
      'item_name': 'model',
      'title': 'Model',
      'hint_detail': 'Vehicle model',
      'hint_panel' : '',
      'example': '',
      'sequence': 3,
      'show_more': true,
      'lookup': null,
      'sortCol': 'model',
    },
    {
      // 'item_name': 'app.subModel.name',
      'item_name': 'sub_model',
      'title': 'Sub model',
      'hint_detail': 'Vehicle sub model',
      'hint_panel' : '',
      'example': '',
      'sequence': 4,
      'show_more': true,
      'lookup': null,
      'sortCol': 'submodel',
    },
    {
      // 'item_name': 'app.notes',
      'item_name': 'assignment_values.notes',
      'title': 'Notes',
      'hint_detail': 'Fitment notes',
      'hint_panel' : '',
      'example': '',
      'sequence': 5,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.block_type',
      'item_name': 'engine_block_type',
      'title': 'Block type',
      'hint_detail': 'Engine block type',
      'hint_panel' : '',
      'example': '',
      'sequence': 6,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.cc',
      'item_name': 'engine_cc',
      'title': 'Cubic centimeters (CC)',
      'hint_detail': 'Engine cubic centimeters',
      'hint_panel' : '',
      'example': '',
      'sequence': 7,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.bore_inch',
      'item_name': 'engine_bore_in',
      'title': 'Engine bore in.',
      'hint_detail': 'Engine bore inches',
      'hint_panel' : '',
      'example': '',
      'sequence': 8,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.bore_metric',
      'item_name': 'engine_bore_mt',
      'title': 'Engine bore metric',
      'hint_detail': 'Engine bore metic',
      'hint_panel' : '',
      'example': '',
      'sequence': 9,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.stroke_inch',
      'item_name': 'engine_stroke_in',
      'title': 'Engine stroke in.',
      'hint_detail': 'Engine stroke inches',
      'hint_panel' : '',
      'example': '',
      'sequence': 10,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.stroke_metric',
      'item_name': 'engine_stroke_mt',
      'title': 'Engine stroke metric',
      'hint_detail': 'Engine stroke metric',
      'hint_panel' : '',
      'example': '',
      'sequence': 11,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.cylinders',
      'item_name': 'engine_cylinder',
      'title': 'Cylinders',
      'hint_detail': 'Engine cylinders',
      'hint_panel' : '',
      'example': '',
      'sequence': 12,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.cid',
      'item_name': 'engine_cid',
      'title': 'CID',
      'hint_detail': 'Engine CID',
      'hint_panel' : '',
      'example': '',
      'sequence': 13,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineBase.liter',
      'item_name': 'engine_liter',
      'title': 'Engine liters',
      'hint_detail': 'Engine liters',
      'hint_panel' : '',
      'example': '',
      'sequence': 14,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineDesignation.name',
      'item_name': 'engine_designation',
      'title': 'Engine designation',
      'hint_detail': 'Engine designation',
      'hint_panel' : '',
      'example': '',
      'sequence': 15,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineMfr.name',
      'item_name': 'engine_mfr',
      'title': 'Engine manufacturer',
      'hint_detail': 'Engine manufacturer',
      'hint_panel' : '',
      'example': '',
      'sequence': 16,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineVIN.name',
      'item_name': 'engine_vin',
      'title': 'Engine VIN',
      'hint_detail': 'Engine VIN',
      'hint_panel' : '',
      'example': '',
      'sequence': 17,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.engineVersion.name',
      'item_name': 'engine_version',
      'title': 'Engine Version',
      'hint_detail': 'Engine version (e.g. Windsor, Cleveland, Modified, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 18,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.aspiration.name',
      'item_name': 'aspiration_name',
      'title': 'Aspiration',
      'hint_detail': 'Aspiration',
      'hint_panel' : '',
      'example': '',
      'sequence': 19,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.bedLength.length_in',
      'item_name': 'bed_length_in',
      'title': 'Bed length (in)',
      'hint_detail': 'Pickup bed length represented in inches to one decimal point of precision',
      'hint_panel' : '',
      'example': '',
      'sequence': 20,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.bedLength.length_metric',
      'item_name': 'bed_length_mt',
      'title': 'Bed length (cm)',
      'hint_detail': 'Pickup bed length represented in centimeters to one decimal point of precision',
      'hint_panel' : '',
      'example': '',
      'sequence': 21,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.bedType.name',
      'item_name': 'bed_type',
      'title': 'Bed type',
      'hint_detail': 'Marketing or industry accepted terminology for bed type (e.g., Styleside or Fleetside)',
      'hint_panel' : '',
      'example': '',
      'sequence': 22,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.bodyNumDoor.name',
      'item_name': 'body_number_doors',
      'title': 'No. of doors',
      'hint_detail': 'Number of doors ranging from 0 to 6',
      'hint_panel' : '',
      'example': '',
      'sequence': 23,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.bodyType.name',
      'item_name': 'body_type',
      'title': 'Body type',
      'hint_detail': 'Industry accepted name for body type (e.g., coupe, sedan, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 24,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.brakeABS.name',
      'item_name': 'brake_abs',
      'title': 'Brake ABS',
      'hint_detail': 'Brake ABS options (e.g., non-ABS, 2-wheel ABS, or 4-wheel ABS)',
      'hint_panel' : '',
      'example': '',
      'sequence': 25,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.brakeSystem.name',
      'item_name': 'brake_system',
      'title': 'Brake system',
      'hint_detail': 'Brake system types (e.g., manual or power)',
      'hint_panel' : '',
      'example': '',
      'sequence': 26,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.cylinderHeadType.name',
      'item_name': 'cylinder_head_type',
      'title': 'Cylinder head type',
      'hint_detail': 'Value of cylinder head name (e.g., SOHC - single overhead cam, DOHC - dual overhead cam, and OHV - overhead valve)',
      'hint_panel' : '',
      'example': '',
      'sequence': 27,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.driveType.name',
      'item_name': 'drive_type',
      'title': 'Drive type',
      'hint_detail': 'Description of drive type (e.g., FWD - front wheel drive, RWD - rear wheel drive, AWD - all wheel drive, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 28,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.elecControlled.name',
      'item_name': 'elec_controlled',
      'title': 'Transmission electronically controlled',
      'hint_detail': 'Yes or No on whether transmission is electronically controlled.',
      'hint_panel' : '',
      'example': '',
      'sequence': 29,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.frontBrakeType.name',
      'item_name': 'front_brake_type',
      'title': 'Front brake',
      'hint_detail': 'Brake type used in the front of vehicle (e.g., disc or drum)',
      'hint_panel' : '',
      'example': '',
      'sequence': 30,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.frontSpringType.namename',
      'item_name': 'front_spring_type',
      'title': 'Front spring type',
      'hint_detail': 'Suspension spring type in the front of the vehicle (e.g., coil, leaf, air, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 31,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.fuelDeliverySubType.name',
      'item_name': 'fuel_delivery_sub_type',
      'title': 'Fuel delivery type',
      'hint_detail': 'Fuel system configuration (e.g., 2BBL - 2 barrel carb, TBI - throttle body injection, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 32,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.fuelDeliveryType.name',
      'item_name': 'fuel_delivery_type',
      'title': 'Fuel delivery system',
      'hint_detail': 'Fuel delivery method (e.g., carburetion, fuel injection, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 33,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.fuelSystemControlType.name',
      'item_name': 'fuel_system_control_type',
      'title': 'Fuel system control type',
      'hint_detail': 'Fuel system method of control (e.g., electronic, mechanical, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 34,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.fuelSystemDesign.name',
      'item_name': 'fuel_system_design',
      'title': 'Fuel engine control unit',
      'hint_detail': 'The design or manufacturer of the fuel system (e.g., Holley, Bosch, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 35,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.fuelType.name',
      'item_name': 'fuel_type',
      'title': 'Fuel type',
      'hint_detail': 'Fuel type used in the vehicle (e.g., flex, diesel, gas, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 36,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.powerOutput.horse_power',
      'item_name': 'horse_power',
      'title': 'Horsepower',
      'hint_detail': 'Engine power output expressed in horsepower, including a decimal point only when required to represent fractional values.',
      'hint_panel' : '',
      'example': '',
      'sequence': 37,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.ignitionSystemType.name',
      'item_name': 'ignition_system_type',
      'title': 'Ignition system type',
      'hint_detail': 'Basic ignition system type (e.g., Distributor-Breaker, Distributorless, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 38,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.powerOutput.kilowatt_power',
      'item_name': 'kilowatt_power',
      'title': 'Kilowatt power',
      'hint_detail': 'Engine power output expressed in kilowatt, including a decimal point only when required to represent fractional values.',
      'hint_panel' : '',
      'example': '',
      'sequence': 39,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.mfrBodyCode.name',
      'item_name': 'mfr_body_code',
      'title': 'Manufacturer body code',
      'hint_detail': 'OE manufacturers unique code for body platform (e.g., A body, F body, C platform, etc.) Note that not all manufacturers designate body codes.',
      'hint_panel' : '',
      'example': '',
      'sequence': 40,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.rearBrakeType.name',
      'item_name': 'rear_brake_type',
      'title': 'Rear brake type',
      'hint_detail': 'Brake type used in the rear of vehicle (e.g., disc or drum)',
      'hint_panel' : '',
      'example': '',
      'sequence': 41,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.rearSpringType.name',
      'item_name': 'rear_spring_type',
      'title': 'Rear spring type',
      'hint_detail': 'Suspension spring type in the rear of the vehicle (e.g., coil, leaf, air, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 42,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.region.id',
      'item_name': 'assignment_values.region_id',
      'title': 'Region',
      'hint_detail': 'Regions currently covered in the Auto Care vehicle database (e.g., USA, Canada, and Mexico)',
      'hint_panel' : '',
      'example': '',
      'sequence': 43,
      'show_more': false,
      'lookup': countryLookup,
    },
    {
      // 'item_name': 'app.steeringSystem.name',
      'item_name': 'steering_system',
      'title': 'Steering system',
      'hint_detail': 'Steering system types (e.g., manual or power)',
      'hint_panel' : '',
      'example': '',
      'sequence': 44,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.steeringType.name',
      'item_name': 'steering_type',
      'title': 'Steering type',
      'hint_detail': 'Steering types (e.g., gear or rack)',
      'hint_panel' : '',
      'example': '',
      'sequence': 45,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.transmissionControlType.name',
      'item_name': 'transmission_control_type',
      'title': 'Transmission control type',
      'hint_detail': 'Type of transmission or transaxle (e.g., automatic, standard, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 46,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.transmissionMfrCode.name',
      'item_name': 'transmission_mfr_code',
      'title': 'Transmission manufacturer',
      'hint_detail': 'Manufacturer transmission model or unit designation (e.g., AW70, 4HP22H, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 47,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.transmissionNumSpeed.name',
      'item_name': 'transmission_num_speeds',
      'title': 'No. of speeds',
      'hint_detail': 'Number of forward speeds for transmission or transaxle (e.g., 3, 4, 5, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 48,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.transmissionType.name',
      'item_name': 'transmission_type',
      'title': 'Transmission type',
      'hint_detail': 'Transmission or transaxle',
      'hint_panel' : '',
      'example': '',
      'sequence': 49,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.valve.name',
      'item_name': 'valves_per_engine',
      'title': 'Valves per engine',
      'hint_detail': 'Total number of intake and exhaust vales in the engine (e.g., 8, 16, 24, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 50,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.baseVehicle.model.vehicleType.name',
      'item_name': 'vehicle_type',
      'title': 'Vehicle type',
      'hint_detail': 'Types of vehicles (e.g., motorcycle, car, truck, etc.)',
      'hint_panel' : '',
      'example': '',
      'sequence': 51,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.wheelBase.wheel_base_in',
      'item_name': 'wheel_base_in',
      'title': 'Wheelbase (in)',
      'hint_detail': 'Wheelbase length represented in inches to one decimal point of precision.',
      'hint_panel' : '',
      'example': '',
      'sequence': 52,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.wheelBase.wheel_base_metric',
      'item_name': 'wheel_base_mt',
      'title': 'Wheelbase (cm)',
      'hint_detail': 'Wheelbase length represented in centimeters to one decimal point of precision.',
      'hint_panel' : '',
      'example': '',
      'sequence': 53,
      'show_more': false,
      'lookup': null,
    },
    {
      // 'item_name': 'app.position.position',
      'item_name': 'assignment_values.position_id',
      'title': 'Position',
      'hint_detail': 'Position',
      'hint_panel' : '',
      'example': '',
      'sequence': 55,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.mfr_label._id',
      'item_name': 'assignment_values.mfr_label',
      'title': 'Manufacturer label',
      'hint_detail': 'Manufacturer label',
      'hint_panel' : '',
      'example': '',
      'sequence': 56,
      'show_more': false,
      'lookup': null
    },
    {
      // 'item_name': 'app.qty',
      'item_name': 'assignment_values.qty',
      'title': 'Per Car Quantity',
      'hint_detail': 'Per Car Quantity',
      'hint_panel' : '',
      'example': '',
      'sequence': 57,
      'show_more': false,
      'lookup': null
    },
  ],
  "hazardous_materials": [],
  'marketing' : [
    {
      'item_name': 'assignment_code',
      'title': 'Assignment Code',
      'hint_detail': 'Assignment Code',
      'hint_panel' : 'Assignment Code',
      'sequence': 1,
      'not_bulk_editable': true,
      'show_more': true,
    },
    {
      'item_name': 'assignment_title',
      'title': 'Assignment Title',
      'hint_detail': 'Assignment Title',
      'hint_panel' : 'Assignment Title',
      'sequence': 2,
      'not_bulk_editable': true,
      'show_more': true,
    },
    {
      'item_name': 'code',
      'title': 'Code',
      'hint_detail': 'Code',
      'hint_panel' : 'Code',
      'sequence': 3,
      'not_bulk_editable': true,
      'show_more': true,
      lookup: marketingCodeLookup,
    },
    {
      'item_name': 'type',
      'title': 'Type',
      'hint_detail': 'Type',
      'hint_panel' : 'Type',
      'sequence': 4,
      'not_bulk_editable': true,
      'show_more': true,
      lookup: marketingTypeLookup,
    },
    {
      'item_name': 'title',
      'title': 'Title',
      'hint_detail': 'Title',
      'hint_panel' : 'Title',
      'sequence': 5,
      'not_bulk_editable': true,
      'show_more': true,
    },

  ],
};
/* eslint-enable */
